import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clientsAPI, importAPI, templatesAPI, mappingsAPI, reportsAPI, trendAPI, qboAPI, aiAPI } from '../lib/api';
import { COLORS, formatCurrency, formatPercent, sumAccounts, sumAccountsPrior, getAccountById } from '../lib/utils';
import { runValidationTies, isPeriodUnsettled, describeFetchFailure, isUsableDashboardPeriod, buildBalanceSheet, buildIncomeStatement, buildCashFlow, buildT12, sectionTotal, defaultPeriodLabels, bsNetIncomeAnchor } from '../lib/reports';
import { computeKpis, KPI_GROUPS } from '../lib/kpis';
import { buildPackagePayload, EMITTER_VERSION } from '../lib/statementPayload';
import { StructuredItems } from '../components/dashboard/StructuredSection';
import ValidationBanner from '../components/dashboard/ValidationBanner';
import DataCompletenessBanner from '../components/dashboard/DataCompletenessBanner';
import { analyzeMonthCompleteness } from '../lib/monthCompleteness';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
  Loader2, Cloud, HardDrive, Upload, RefreshCw, Check, X, 
  ChevronRight, Edit2, Plus, FileText, Send, Download, LogOut, Users, Printer, ChevronDown, Building2,
  GripVertical, ChevronUp, Sparkles, LifeBuoy, History, Settings, AlertTriangle, Bug
} from 'lucide-react';
import FathomMapLayer from '../components/dashboard/FathomMapLayer';
import CompaniesLandingPage from '../components/dashboard/CompaniesLandingPage';
import MappingHistoryModal from '../components/MappingHistoryModal';
import EngagementSettingsModal from '../components/EngagementSettingsModal';
import ConsolidatedReportLayer from '../components/dashboard/ConsolidatedReportLayer';
import QboFileAudit from '../components/dashboard/QboFileAudit';

const SMJ_LOGO_URL = "https://customer-assets.emergentagent.com/job_funds-portal/artifacts/xi0cc8ut_Clean%20Logo%20with%20Words.png";

// Sample data (will be replaced by API data)
const DEFAULT_ACCOUNTS = [
  {"id": "1000", "name": "Operating Cash", "type": "Asset", "balance": 214580},
  {"id": "1010", "name": "Payroll Cash", "type": "Asset", "balance": 45220},
  {"id": "1020", "name": "Savings Account", "type": "Asset", "balance": 125000},
  {"id": "1100", "name": "Accounts Receivable", "type": "Asset", "balance": 187450},
  {"id": "1200", "name": "Inventory", "type": "Asset", "balance": 94300},
  {"id": "1500", "name": "Equipment", "type": "Asset", "balance": 285000},
  {"id": "1510", "name": "Accum. Depr. - Equipment", "type": "Asset", "balance": -68400},
  {"id": "1600", "name": "Building", "type": "Asset", "balance": 520000},
  {"id": "1610", "name": "Accum. Depr. - Building", "type": "Asset", "balance": -78000},
  {"id": "2000", "name": "Accounts Payable", "type": "Liability", "balance": 63200},
  {"id": "2100", "name": "Accrued Payroll", "type": "Liability", "balance": 28500},
  {"id": "2110", "name": "Accrued Liabilities", "type": "Liability", "balance": 15800},
  {"id": "2200", "name": "Line of Credit", "type": "Liability", "balance": 75000},
  {"id": "2300", "name": "Note Payable - Equipment", "type": "Liability", "balance": 142000},
  {"id": "2310", "name": "Note Payable - Building", "type": "Liability", "balance": 385000},
  {"id": "3000", "name": "Common Stock", "type": "Equity", "balance": 100000},
  {"id": "3100", "name": "Retained Earnings", "type": "Equity", "balance": 435650},
  {"id": "4000", "name": "Product Sales", "type": "Income", "balance": 1245000},
  {"id": "4010", "name": "Service Revenue - Consulting", "type": "Income", "balance": 485000},
  {"id": "4020", "name": "Service Revenue - Maintenance", "type": "Income", "balance": 162000},
  {"id": "4050", "name": "Other Income", "type": "Income", "balance": 18500},
  {"id": "4900", "name": "Sales Returns & Allowances", "type": "Income", "balance": -32400},
  {"id": "5000", "name": "Cost of Goods Sold - Materials", "type": "COGS", "balance": 498000},
  {"id": "5010", "name": "Cost of Goods Sold - Labor", "type": "COGS", "balance": 312000},
  {"id": "5020", "name": "Cost of Goods Sold - Other", "type": "COGS", "balance": 87600},
  {"id": "6000", "name": "Salaries & Wages", "type": "Expense", "balance": 285000},
  {"id": "6010", "name": "Payroll Taxes", "type": "Expense", "balance": 21800},
  {"id": "6020", "name": "Employee Benefits", "type": "Expense", "balance": 42500},
  {"id": "6030", "name": "Retirement Contributions", "type": "Expense", "balance": 14250},
  {"id": "6100", "name": "Rent Expense", "type": "Expense", "balance": 0},
  {"id": "6110", "name": "Utilities", "type": "Expense", "balance": 18400},
  {"id": "6120", "name": "Property Taxes", "type": "Expense", "balance": 12600},
  {"id": "6200", "name": "Advertising", "type": "Expense", "balance": 28500},
  {"id": "6210", "name": "Marketing", "type": "Expense", "balance": 15200},
  {"id": "6300", "name": "Insurance", "type": "Expense", "balance": 24800},
  {"id": "6310", "name": "Professional Fees", "type": "Expense", "balance": 18500},
  {"id": "6400", "name": "Depreciation Expense", "type": "Expense", "balance": 38200},
  {"id": "6500", "name": "Office Supplies", "type": "Expense", "balance": 8400},
  {"id": "6510", "name": "Software & Subscriptions", "type": "Expense", "balance": 12800},
  {"id": "6520", "name": "Miscellaneous", "type": "Expense", "balance": 4200},
  {"id": "6900", "name": "Interest Expense", "type": "Expense", "balance": 28400},
];

const DEFAULT_TREND = [
  {"month": "Jan", "revenue": 142000, "netIncome": 18200},
  {"month": "Feb", "revenue": 138000, "netIncome": 16800},
  {"month": "Mar", "revenue": 165000, "netIncome": 24500},
  {"month": "Apr", "revenue": 158000, "netIncome": 21200},
  {"month": "May", "revenue": 172000, "netIncome": 28400},
  {"month": "Jun", "revenue": 168000, "netIncome": 26100},
  {"month": "Jul", "revenue": 145000, "netIncome": 19800},
  {"month": "Aug", "revenue": 152000, "netIncome": 22500},
  {"month": "Sep", "revenue": 178000, "netIncome": 31200},
  {"month": "Oct", "revenue": 185000, "netIncome": 34800},
  {"month": "Nov", "revenue": 192000, "netIncome": 38500},
  {"month": "Dec", "revenue": 283100, "netIncome": 58750},
];

// V19.10.6 — Format a Date or ISO string in America/Chicago (Central
// Time) for display in the top-bar "Last sync" pill. Returns a short
// human-friendly string like "Today, 2:14 PM CT" or "May 26, 2:14 PM CT"
// depending on whether the moment is today or earlier.
const formatCentralTime = (when) => {
  if (!when) return '—';
  const d = when instanceof Date ? when : new Date(when);
  if (Number.isNaN(d.getTime())) return '—';
  const tz = 'America/Chicago';
  const today = new Date();
  const sameDay = (
    d.toLocaleDateString('en-US', { timeZone: tz })
    === today.toLocaleDateString('en-US', { timeZone: tz })
  );
  const timePart = d.toLocaleTimeString('en-US', {
    timeZone: tz, hour: 'numeric', minute: '2-digit',
  });
  if (sameDay) return `Today, ${timePart} CT`;
  const datePart = d.toLocaleDateString('en-US', {
    timeZone: tz, month: 'short', day: 'numeric',
  });
  return `${datePart}, ${timePart} CT`;
};

const DEFAULT_TEMPLATES = {
  "Service Business": {
    income: [
      { label: "Service Revenue", accounts: ["4010", "4020"] },
      { label: "Product Sales", accounts: ["4000", "4900"] },
      { label: "Other Income", accounts: ["4050"] }
    ],
    cogs: [
      { label: "Direct Materials", accounts: ["5000"] },
      { label: "Direct Labor", accounts: ["5010"] },
      { label: "Other Cost of Sales", accounts: ["5020"] }
    ],
    opex: [
      { label: "Compensation & Benefits", accounts: ["6000", "6010", "6020", "6030"] },
      { label: "Occupancy", accounts: ["6100", "6110", "6120"] },
      { label: "Marketing & Advertising", accounts: ["6200", "6210"] },
      { label: "Insurance", accounts: ["6300"] },
      { label: "Professional Fees", accounts: ["6310"] },
      { label: "Depreciation & Amortization", accounts: ["6400"] },
      { label: "G&A - Other", accounts: ["6500", "6510", "6520"] }
    ],
    other: [
      { label: "Interest Expense", accounts: ["6900"] }
    ],
    assets_current: [
      { label: "Cash & Equivalents", accounts: ["1000", "1010", "1020"] },
      { label: "Accounts Receivable", accounts: ["1100"] },
      { label: "Inventory", accounts: ["1200"] }
    ],
    assets_fixed: [
      { label: "Equipment", accounts: ["1500", "1510"] },
      { label: "Buildings & Land", accounts: ["1600", "1610"] }
    ],
    liabilities_current: [
      { label: "Accounts Payable", accounts: ["2000"] },
      { label: "Accrued Payroll", accounts: ["2100"] },
      { label: "Accrued Liabilities", accounts: ["2110"] },
      { label: "Line of Credit", accounts: ["2200"] }
    ],
    liabilities_long: [
      { label: "Note Payable - Equipment", accounts: ["2300"] },
      { label: "Note Payable - Building", accounts: ["2310"] }
    ],
    equity: [
      { label: "Capital", accounts: ["3000"] },
      { label: "Retained Earnings", accounts: ["3100"] }
    ]
  },
  "Contractor": {
    income: [
      { label: "Contract Revenue", accounts: ["4010", "4020"] },
      { label: "Change Orders", accounts: ["4000"] },
      { label: "Retainage Earned", accounts: ["4050", "4900"] }
    ],
    cogs: [
      { label: "Job Materials", accounts: ["5000"] },
      { label: "Subcontractor Costs", accounts: ["5010"] },
      { label: "Equipment Rental", accounts: ["5020"] }
    ],
    opex: [
      { label: "Compensation & Benefits", accounts: ["6000", "6010", "6020", "6030"] },
      { label: "Vehicle & Equipment", accounts: ["6100", "6110"] },
      { label: "Insurance & Bonding", accounts: ["6300", "6310"] },
      { label: "Licenses & Permits", accounts: ["6120"] },
      { label: "Depreciation", accounts: ["6400"] },
      { label: "Office & Admin", accounts: ["6200", "6210", "6500", "6510", "6520"] }
    ],
    other: [
      { label: "Interest Expense", accounts: ["6900"] }
    ],
    assets_current: [
      { label: "Cash & Equivalents", accounts: ["1000", "1010", "1020"] },
      { label: "Accounts Receivable", accounts: ["1100"] },
      { label: "Retainage Receivable", accounts: ["1200"] }
    ],
    assets_fixed: [
      { label: "Equipment & Vehicles", accounts: ["1500", "1510"] },
      { label: "Buildings & Land", accounts: ["1600", "1610"] }
    ],
    liabilities_current: [
      { label: "Accounts Payable", accounts: ["2000"] },
      { label: "Accrued Liabilities", accounts: ["2100", "2110"] },
      { label: "Line of Credit", accounts: ["2200"] }
    ],
    liabilities_long: [
      { label: "Equipment Loans", accounts: ["2300"] },
      { label: "Mortgage Payable", accounts: ["2310"] }
    ],
    equity: [
      { label: "Capital", accounts: ["3000"] },
      { label: "Retained Earnings", accounts: ["3100"] }
    ]
  },
  "Retail": {
    income: [
      { label: "Merchandise Sales", accounts: ["4000", "4010"] },
      { label: "Online Sales", accounts: ["4020"] },
      { label: "Shipping Revenue", accounts: ["4050", "4900"] }
    ],
    cogs: [
      { label: "Cost of Goods Sold", accounts: ["5000", "5010"] },
      { label: "Freight In", accounts: ["5020"] }
    ],
    opex: [
      { label: "Payroll & Benefits", accounts: ["6000", "6010", "6020", "6030"] },
      { label: "Rent & Utilities", accounts: ["6100", "6110", "6120"] },
      { label: "Advertising & Marketing", accounts: ["6200", "6210"] },
      { label: "Insurance", accounts: ["6300"] },
      { label: "Credit Card Fees", accounts: ["6310"] },
      { label: "Depreciation", accounts: ["6400"] },
      { label: "Store Supplies & Other", accounts: ["6500", "6510", "6520"] }
    ],
    other: [
      { label: "Interest Expense", accounts: ["6900"] }
    ],
    assets_current: [
      { label: "Cash & Equivalents", accounts: ["1000", "1010", "1020"] },
      { label: "Accounts Receivable", accounts: ["1100"] },
      { label: "Merchandise Inventory", accounts: ["1200"] }
    ],
    assets_fixed: [
      { label: "Fixtures & Equipment", accounts: ["1500", "1510"] },
      { label: "Leasehold Improvements", accounts: ["1600", "1610"] }
    ],
    liabilities_current: [
      { label: "Accounts Payable - Trade", accounts: ["2000"] },
      { label: "Accrued Expenses", accounts: ["2100", "2110"] },
      { label: "Line of Credit", accounts: ["2200"] }
    ],
    liabilities_long: [
      { label: "Equipment Financing", accounts: ["2300"] },
      { label: "Long-Term Debt", accounts: ["2310"] }
    ],
    equity: [
      { label: "Capital", accounts: ["3000"] },
      { label: "Retained Earnings", accounts: ["3100"] }
    ]
  },
  "Manufacturing": {
    income: [
      { label: "Finished Goods Sales", accounts: ["4000", "4010"] },
      { label: "Custom Orders", accounts: ["4020"] },
      { label: "Scrap Sales", accounts: ["4050", "4900"] }
    ],
    cogs: [
      { label: "Raw Materials", accounts: ["5000"] },
      { label: "Direct Labor", accounts: ["5010"] },
      { label: "Manufacturing Overhead", accounts: ["5020"] }
    ],
    opex: [
      { label: "Administrative Salaries", accounts: ["6000", "6010"] },
      { label: "Benefits & Payroll Taxes", accounts: ["6020", "6030"] },
      { label: "Facility Costs", accounts: ["6100", "6110", "6120"] },
      { label: "Sales & Marketing", accounts: ["6200", "6210"] },
      { label: "Insurance", accounts: ["6300", "6310"] },
      { label: "Depreciation", accounts: ["6400"] },
      { label: "General & Administrative", accounts: ["6500", "6510", "6520"] }
    ],
    other: [
      { label: "Interest Expense", accounts: ["6900"] }
    ],
    assets_current: [
      { label: "Cash & Equivalents", accounts: ["1000", "1010", "1020"] },
      { label: "Accounts Receivable", accounts: ["1100"] },
      { label: "Inventory - Raw Materials", accounts: ["1200"] }
    ],
    assets_fixed: [
      { label: "Machinery & Equipment", accounts: ["1500", "1510"] },
      { label: "Building & Land", accounts: ["1600", "1610"] }
    ],
    liabilities_current: [
      { label: "Accounts Payable", accounts: ["2000"] },
      { label: "Accrued Expenses", accounts: ["2100", "2110"] },
      { label: "Line of Credit", accounts: ["2200"] }
    ],
    liabilities_long: [
      { label: "Equipment Loans", accounts: ["2300"] },
      { label: "Mortgage Payable", accounts: ["2310"] }
    ],
    equity: [
      { label: "Capital", accounts: ["3000"] },
      { label: "Retained Earnings", accounts: ["3100"] }
    ]
  },
  "Agriculture": {
    income: [
      { label: "Crop Sales", accounts: ["4000", "4010"] },
      { label: "Livestock Sales", accounts: ["4020"] },
      { label: "Government Payments", accounts: ["4050", "4900"] }
    ],
    cogs: [
      { label: "Seed & Fertilizer", accounts: ["5000"] },
      { label: "Feed & Livestock Costs", accounts: ["5010"] },
      { label: "Fuel & Repairs", accounts: ["5020"] }
    ],
    opex: [
      { label: "Labor & Benefits", accounts: ["6000", "6010", "6020", "6030"] },
      { label: "Land Rent & Property Tax", accounts: ["6100", "6110", "6120"] },
      { label: "Insurance", accounts: ["6300", "6310"] },
      { label: "Depreciation", accounts: ["6400"] },
      { label: "Utilities & Other", accounts: ["6200", "6210", "6500", "6510", "6520"] }
    ],
    other: [
      { label: "Interest Expense", accounts: ["6900"] }
    ],
    assets_current: [
      { label: "Cash & Equivalents", accounts: ["1000", "1010", "1020"] },
      { label: "Accounts Receivable", accounts: ["1100"] },
      { label: "Inventory & Livestock", accounts: ["1200"] }
    ],
    assets_fixed: [
      { label: "Equipment & Vehicles", accounts: ["1500", "1510"] },
      { label: "Land & Buildings", accounts: ["1600", "1610"] }
    ],
    liabilities_current: [
      { label: "Accounts Payable", accounts: ["2000"] },
      { label: "Operating Line of Credit", accounts: ["2100", "2110", "2200"] }
    ],
    liabilities_long: [
      { label: "Equipment Notes", accounts: ["2300"] },
      { label: "Real Estate Loans", accounts: ["2310"] }
    ],
    equity: [
      { label: "Capital", accounts: ["3000"] },
      { label: "Retained Earnings", accounts: ["3100"] }
    ]
  }
};

// Tag component
const Tag = ({ children, color = COLORS.primary, style = {} }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 6,
    background: color,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    ...style,
  }}>
    {children}
  </span>
);

// Card component
const Card = ({ children, style = {}, ...props }) => (
  <div 
    style={{
      background: COLORS.card,
      borderRadius: 12,
      border: `1px solid ${COLORS.border}`,
      padding: 24,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

// ═══════════════════════════════════════════════════
//   IMPORT LAYER
// ═══════════════════════════════════════════════════
function ImportLayer({ onComplete, clientId }) {
  const [source, setSource] = useState('qbo');
  const [syncMode, setSyncMode] = useState('daily');
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  // V15.2: hold the latest sync metadata locally so the Confirm step
  // forwards it to the dashboard's qboMeta. Previously `onComplete(accounts)`
  // discarded period_start_eve / ni_at_period_start / cash_at_period_start
  // and the CF fell back to V15 12-month mode silently.
  const [importMeta, setImportMeta] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [qboStatus, setQboStatus] = useState(null);
  const [connectingQBO, setConnectingQBO] = useState(false);
  // V25.8 — live "which company file is this?" audit. Auto-runs once the
  // connection status resolves so a stale/wrong file is visible immediately.
  const [connAudit, setConnAudit] = useState(null);
  const [connAuditError, setConnAuditError] = useState(null);
  const [connAuditLoading, setConnAuditLoading] = useState(false);

  const runConnectionAudit = React.useCallback(async () => {
    if (!clientId || clientId === 'demo') return;
    setConnAuditLoading(true);
    setConnAuditError(null);
    try {
      const res = await qboAPI.connectionAudit(clientId);
      setConnAudit(res.data);
    } catch (err) {
      setConnAuditError(err?.response?.data?.detail || err.message || 'Could not reach QuickBooks');
    } finally {
      setConnAuditLoading(false);
    }
  }, [clientId]);

  const syncOptions = [
    { id: 'realtime', label: 'Real-time', desc: 'Sync every 15-30 minutes', icon: RefreshCw },
    { id: 'daily', label: 'Daily', desc: 'Sync once per day at 2 AM', icon: RefreshCw },
    { id: 'manual', label: 'Manual', desc: 'Sync only when triggered', icon: RefreshCw },
  ];

  // V25.8 — auto-verify the connected company file once status resolves.
  useEffect(() => {
    if (qboStatus?.is_connected) runConnectionAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qboStatus?.is_connected, runConnectionAudit]);

  // Check QBO connection status and handle OAuth callback
  useEffect(() => {
    const checkQBOStatus = async () => {
      if (clientId && clientId !== 'demo') {
        try {
          const res = await qboAPI.getStatus(clientId);
          setQboStatus(res.data);
          return res.data;
        } catch (err) {
          console.log('QBO status check failed:', err);
        }
      }
      return null;
    };

    const runPostOAuthAutoFlow = async () => {
      // Clear the URL params first
      window.history.replaceState({}, document.title, window.location.pathname);
      const status = await checkQBOStatus();
      if (!status?.is_connected) return;
      // Auto-sync accounts and advance to Map step with AI mapping flag
      setLoading(true);
      try {
        const res = await qboAPI.getAccounts(clientId);
        sessionStorage.setItem('smj_auto_ai_map', '1');
        onComplete(res.data.accounts, {
          reconciliation: res.data.reconciliation,
          completeness:   res.data.completeness,
          period_start:   res.data.period_start,
          period_end:     res.data.period_end,
          period_start_eve:     res.data.period_start_eve,
          ni_at_period_start:   res.data.ni_at_period_start,
          cash_at_period_start: res.data.cash_at_period_start,
          period_diagnostics:   res.data.period_diagnostics,
          pl_period:            res.data.pl_period,
          pl_month:             res.data.pl_month,
          pl_month_prior:       res.data.pl_month_prior,
          fetch_reports:        res.data.fetch_reports,
        });
      } catch (err) {
        console.error('Post-OAuth account sync failed:', err);
        alert('Connected to QuickBooks, but failed to load accounts. Click "Sync & Continue" to retry.');
      } finally {
        setLoading(false);
      }
    };

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('qbo_connected') === 'true') {
      runPostOAuthAutoFlow();
    } else {
      checkQBOStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleConnectQBO = async () => {
    setConnectingQBO(true);
    try {
      // Build redirect URI - use the current origin plus /api/qbo/callback
      const redirectUri = `${process.env.REACT_APP_BACKEND_URL}/api/qbo/callback`;
      // Pass the frontend origin so the backend redirects the user back to
      // the SAME hostname they came from after QBO completes — not to the
      // backend's own emergent.host hostname (which has no portal session).
      const frontendOrigin = window.location.origin;
      
      const res = await qboAPI.getAuthUrl(clientId, redirectUri, frontendOrigin);
      
      // Redirect to QuickBooks OAuth page
      window.location.href = res.data.authorization_url;
    } catch (err) {
      console.error('Failed to get QBO auth URL:', err);
      alert('Failed to connect to QuickBooks. Please try again.');
      setConnectingQBO(false);
    }
  };

  const handleQBOSync = async () => {
    setLoading(true);
    try {
      // If connected to QBO, fetch real accounts
      if (qboStatus?.is_connected) {
        const res = await qboAPI.getAccounts(clientId);
        setAccounts(res.data.accounts);
        setImportMeta({
          reconciliation: res.data.reconciliation,
          completeness:   res.data.completeness,
          period_start:   res.data.period_start,
          period_end:     res.data.period_end,
          period_start_eve:     res.data.period_start_eve,
          ni_at_period_start:   res.data.ni_at_period_start,
          cash_at_period_start: res.data.cash_at_period_start,
          period_diagnostics:   res.data.period_diagnostics,
          pl_period:            res.data.pl_period,
          pl_month:             res.data.pl_month,
          pl_month_prior:       res.data.pl_month_prior,
          fetch_reports:        res.data.fetch_reports,
        });
      } else {
        // Use import sync which falls back to sample data
        const res = await importAPI.syncQBO(clientId);
        setAccounts(res.data.accounts);
        setImportMeta(null);
      }
      setStep(1);
    } catch (err) {
      console.error('Sync failed:', err);
      // Use default data for demo
      setAccounts(DEFAULT_ACCOUNTS);
      setImportMeta(null);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    setLoading(true);
    try {
      const res = await importAPI.uploadQBD(clientId, file);
      setAccounts(res.data.accounts);
      setImportMeta(null);
      setStep(1);
    } catch (err) {
      console.error('Upload failed:', err);
      setAccounts(DEFAULT_ACCOUNTS);
      setImportMeta(null);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleConfirm = () => {
    onComplete(accounts, importMeta);
  };

  const handleDisconnectQBO = async () => {
    if (window.confirm('Are you sure you want to disconnect QuickBooks?')) {
      try {
        await qboAPI.disconnect(clientId);
        setQboStatus({ ...qboStatus, is_connected: false });
      } catch (err) {
        console.error('Failed to disconnect QBO:', err);
      }
    }
  };

  return (
    <div data-testid="import-layer" style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <Tag>Step 1 of 3</Tag>
        <h2 className="font-heading" style={{ fontSize: 28, fontWeight: 500, color: COLORS.dark, marginTop: 12 }}>
          Import Financial Data
        </h2>
        <p style={{ color: COLORS.muted, marginTop: 8 }}>
          Connect to QuickBooks or upload an exported trial balance file.
        </p>
      </div>

      {step === 0 && (
        <>
          {/* Source Selection */}
          <Card style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 16 }}>
              Data Source
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <button
                data-testid="source-qbo-btn"
                onClick={() => setSource('qbo')}
                style={{
                  padding: 20,
                  borderRadius: 10,
                  border: `2px solid ${source === 'qbo' ? COLORS.primary : COLORS.border}`,
                  background: source === 'qbo' ? COLORS.primaryLight : COLORS.card,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <Cloud size={24} color={source === 'qbo' ? COLORS.primary : COLORS.muted} />
                <div style={{ marginTop: 12, fontSize: 16, fontWeight: 600, color: COLORS.dark }}>QuickBooks Online</div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>Connect via OAuth for automatic sync</div>
                {qboStatus?.is_connected && (
                  <div style={{ marginTop: 8 }}>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      background: '#DCFCE7', 
                      color: COLORS.success, 
                      fontSize: 10, 
                      fontWeight: 600 
                    }}>
                      <Check size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      CONNECTED
                    </span>
                  </div>
                )}
              </button>
              <button
                data-testid="source-qbd-btn"
                onClick={() => setSource('qbd')}
                style={{
                  padding: 20,
                  borderRadius: 10,
                  border: `2px solid ${source === 'qbd' ? COLORS.primary : COLORS.border}`,
                  background: source === 'qbd' ? COLORS.primaryLight : COLORS.card,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <HardDrive size={24} color={source === 'qbd' ? COLORS.primary : COLORS.muted} />
                <div style={{ marginTop: 12, fontSize: 16, fontWeight: 600, color: COLORS.dark }}>QuickBooks Desktop</div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>Upload exported trial balance file</div>
              </button>
            </div>
          </Card>

          {/* QBO Connection Status & Actions */}
          {source === 'qbo' && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 16 }}>
                QuickBooks Online Connection
              </div>
              
              {qboStatus?.is_connected ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: 8, 
                      background: '#DCFCE7', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Check size={24} color={COLORS.success} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark }} data-testid="qbo-connected-company-name">
                        {connAudit?.company?.company_name
                          ? `Connected to ${connAudit.company.company_name}`
                          : 'Connected to QuickBooks Online'}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.muted }}>
                        Company ID: {qboStatus.realm_id} • Connected {qboStatus.connected_at ? new Date(qboStatus.connected_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                    <button
                      onClick={handleDisconnectQBO}
                      style={{
                        marginLeft: 'auto',
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: `1px solid ${COLORS.error}`,
                        background: 'transparent',
                        color: COLORS.error,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Disconnect
                    </button>
                  </div>

                  <QboFileAudit
                    audit={connAudit}
                    error={connAuditError}
                    loading={connAuditLoading}
                    onRun={runConnectionAudit}
                  />

                  {/* Sync Options */}
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 12, marginTop: 20 }}>
                    Sync Frequency
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {syncOptions.map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setSyncMode(opt.id)}
                        style={{
                          padding: 16,
                          borderRadius: 8,
                          border: `2px solid ${syncMode === opt.id ? COLORS.primary : COLORS.border}`,
                          background: syncMode === opt.id ? COLORS.primaryLight : COLORS.card,
                          cursor: 'pointer',
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <Cloud size={48} color={COLORS.muted} style={{ marginBottom: 16 }} />
                  <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.dark, marginBottom: 8 }}>
                    Connect to QuickBooks Online
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.muted, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
                    Securely connect your QuickBooks Online account to automatically import your chart of accounts and financial data.
                  </div>
                  <button
                    data-testid="connect-qbo-btn"
                    onClick={handleConnectQBO}
                    disabled={connectingQBO}
                    style={{
                      padding: '14px 32px',
                      borderRadius: 8,
                      background: connectingQBO ? COLORS.muted : '#2CA01C',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      border: 'none',
                      cursor: connectingQBO ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {connectingQBO && <Loader2 size={18} className="animate-spin" />}
                    <Cloud size={18} />
                    {connectingQBO ? 'Connecting...' : 'Connect to QuickBooks'}
                  </button>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 12 }}>
                    You'll be redirected to Intuit to authorize access
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* QBD File Upload */}
          {source === 'qbd' && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 16 }}>
                Upload File
              </div>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                style={{
                  padding: 40,
                  borderRadius: 10,
                  border: `2px dashed ${dragOver ? COLORS.primary : COLORS.border}`,
                  background: dragOver ? COLORS.primaryLight : COLORS.off,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Upload size={32} color={COLORS.muted} style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark }}>
                  Drop your file here or click to browse
                </div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 8 }}>
                  Supports .xlsx and .csv trial balance exports
                </div>
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  style={{
                    display: 'inline-block',
                    marginTop: 16,
                    padding: '10px 24px',
                    borderRadius: 8,
                    background: COLORS.primary,
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Browse Files
                </label>
              </div>
            </Card>
          )}

          {/* Action Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              data-testid="import-sync-btn"
              onClick={source === 'qbo' ? handleQBOSync : null}
              disabled={loading || (source === 'qbd') || (source === 'qbo' && !qboStatus?.is_connected && clientId !== 'demo')}
              style={{
                padding: '14px 32px',
                borderRadius: 8,
                background: loading ? COLORS.muted : (source === 'qbo' && !qboStatus?.is_connected && clientId !== 'demo') ? COLORS.muted : COLORS.primary,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                border: 'none',
                cursor: loading || (source === 'qbd') || (source === 'qbo' && !qboStatus?.is_connected && clientId !== 'demo') ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {source === 'qbo' 
                ? (loading ? 'Syncing...' : qboStatus?.is_connected ? 'Sync & Continue' : (clientId === 'demo' ? 'Use Sample Data' : 'Connect QBO First'))
                : 'Upload file above'}
            </button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          {/* Data Preview */}
          <Card style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted }}>
                  Imported Accounts
                </div>
                <div style={{ fontSize: 13, color: COLORS.dark, marginTop: 4 }}>
                  {accounts.length} accounts found
                </div>
              </div>
              <Tag color={COLORS.success}><Check size={12} style={{ marginRight: 4 }} /> Data Loaded</Tag>
            </div>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                    <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase' }}>ID</th>
                    <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase' }}>Account Name</th>
                    <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase' }}>Type</th>
                    <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.slice(0, 20).map(acc => (
                    <tr key={acc.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '10px 0', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.muted }}>{acc.id}</td>
                      <td style={{ padding: '10px 0', fontSize: 13, color: COLORS.dark }}>{acc.name}</td>
                      <td style={{ padding: '10px 0' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: acc.type === 'Income' ? '#DCFCE7' : acc.type === 'Expense' || acc.type === 'COGS' ? '#FEF2F2' : COLORS.off,
                          color: acc.type === 'Income' ? COLORS.success : acc.type === 'Expense' || acc.type === 'COGS' ? COLORS.error : COLORS.dark,
                          fontSize: 11,
                          fontWeight: 500,
                        }}>
                          {acc.type}
                        </span>
                      </td>
                      <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, color: acc.balance < 0 ? COLORS.error : COLORS.dark }}>
                        {formatCurrency(acc.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {accounts.length > 20 && (
                <div style={{ padding: 12, textAlign: 'center', color: COLORS.muted, fontSize: 12 }}>
                  + {accounts.length - 20} more accounts
                </div>
              )}
            </div>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button
              onClick={() => setStep(0)}
              style={{
                padding: '12px 24px',
                borderRadius: 8,
                border: `2px solid ${COLORS.border}`,
                background: COLORS.card,
                color: COLORS.dark,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ← Back
            </button>
            <button
              data-testid="import-confirm-btn"
              onClick={handleConfirm}
              style={{
                padding: '14px 32px',
                borderRadius: 8,
                background: COLORS.primary,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              Confirm & Continue <ChevronRight size={18} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
//   REMAP LAYER
// ═══════════════════════════════════════════════════
function RemapLayer({ onComplete, accounts, clientId }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('Service Business');
  const [mapping, setMapping] = useState(DEFAULT_TEMPLATES['Service Business']);
  const [activeSection, setActiveSection] = useState('income');
  const [editingLine, setEditingLine] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiMapping, setAiMapping] = useState(false);
  const [draggedAccount, setDraggedAccount] = useState(null);
  const [dragOverLine, setDragOverLine] = useState(null);

  const sections = [
    { key: 'income', label: 'Revenue', color: COLORS.success, category: 'P&L', mappingKey: 'Revenue', allowedTypes: ['Income'] },
    { key: 'cogs', label: 'Cost of Sales', color: COLORS.error, category: 'P&L', mappingKey: 'Cost of Revenue', allowedTypes: ['COGS'] },
    { key: 'opex', label: 'Operating Expenses', color: COLORS.warning, category: 'P&L', mappingKey: 'Operating Expenses', allowedTypes: ['Expense'] },
    { key: 'other', label: 'Other Income/Expense', color: COLORS.muted, category: 'P&L', mappingKey: 'Other Expense', allowedTypes: ['Income', 'Expense', 'Other'] },
    { key: 'assets_current', label: 'Current Assets', color: '#3B82F6', category: 'Balance Sheet', mappingKey: 'Current Assets', allowedTypes: ['Asset'] },
    { key: 'assets_fixed', label: 'Fixed Assets', color: '#6366F1', category: 'Balance Sheet', mappingKey: 'Fixed Assets', allowedTypes: ['Asset'] },
    { key: 'liabilities_current', label: 'Current Liabilities', color: '#EF4444', category: 'Balance Sheet', mappingKey: 'Current Liabilities', allowedTypes: ['Liability'] },
    { key: 'liabilities_long', label: 'Long-Term Liabilities', color: '#DC2626', category: 'Balance Sheet', mappingKey: 'Long-Term Debt', allowedTypes: ['Liability'] },
    { key: 'equity', label: 'Equity', color: '#10B981', category: 'Balance Sheet', mappingKey: 'Equity', allowedTypes: ['Equity'] },
  ];

  // Get color for account type badge
  const getAccountTypeColor = (type) => {
    switch(type) {
      case 'Income': return { bg: '#DCFCE7', color: '#16A34A' };
      case 'COGS': return { bg: '#FEE2E2', color: '#DC2626' };
      case 'Expense': return { bg: '#FEF3C7', color: '#D97706' };
      case 'Asset': return { bg: '#DBEAFE', color: '#2563EB' };
      case 'Liability': return { bg: '#FCE7F3', color: '#DB2777' };
      case 'Equity': return { bg: '#D1FAE5', color: '#059669' };
      default: return { bg: COLORS.off, color: COLORS.muted };
    }
  };

  // Check if an account can be dropped into a section
  const canDropInSection = (accountType, sectionKey) => {
    const section = sections.find(s => s.key === sectionKey);
    if (!section) return false;
    return section.allowedTypes.includes(accountType);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Auto-run AI mapping once after QBO OAuth completes
  useEffect(() => {
    const shouldAutoMap = sessionStorage.getItem('smj_auto_ai_map') === '1';
    if (shouldAutoMap && accounts && accounts.length > 0 && mapping) {
      sessionStorage.removeItem('smj_auto_ai_map');
      handleAIMapping(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const loadTemplates = async () => {
    try {
      const res = await templatesAPI.list();
      setTemplates(res.data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const handleTemplateChange = (name) => {
    setSelectedTemplate(name);
    const template = templates.find(t => t.name === name);
    if (template) {
      setMapping(template.mapping);
    } else if (DEFAULT_TEMPLATES[name]) {
      setMapping(DEFAULT_TEMPLATES[name]);
    }
  };

  // AI Auto-mapping function
  const handleAIMapping = async (silent = false) => {
    setAiMapping(true);
    try {
      // Build template structure for AI
      const templateForAI = {};
      for (const section of sections) {
        if (mapping[section.key]) {
          templateForAI[section.mappingKey] = mapping[section.key];
        }
      }
      
      const res = await aiAPI.mapAccounts(accounts, templateForAI);
      
      if (res.data.success && res.data.suggested_mapping) {
        // Apply the AI suggestions to our mapping
        const newMapping = { ...mapping };
        const suggestedMapping = res.data.suggested_mapping;
        
        // Map the suggested accounts to our section keys
        for (const section of sections) {
          if (newMapping[section.key]) {
            for (let i = 0; i < newMapping[section.key].length; i++) {
              const lineLabel = newMapping[section.key][i].label;
              if (suggestedMapping[lineLabel]) {
                // Add suggested accounts to this line
                const existingAccounts = new Set(newMapping[section.key][i].accounts || []);
                suggestedMapping[lineLabel].forEach(accId => {
                  existingAccounts.add(String(accId));
                });
                newMapping[section.key][i].accounts = Array.from(existingAccounts);
              }
            }
          }
        }
        
        setMapping(newMapping);
        if (!silent) {
          alert('AI mapping suggestions applied! Review the mappings and adjust as needed.');
        }
      }
    } catch (err) {
      console.error('AI mapping failed:', err);
      if (!silent) {
        alert('AI mapping failed. Please map accounts manually.');
      }
    } finally {
      setAiMapping(false);
    }
  };

  const startEdit = (section, index) => {
    setEditingLine({ section, index });
    setEditLabel(mapping[section][index].label);
  };

  const saveEdit = () => {
    if (editingLine) {
      const newMapping = { ...mapping };
      newMapping[editingLine.section][editingLine.index].label = editLabel;
      setMapping(newMapping);
      setEditingLine(null);
    }
  };

  const removeAccount = (section, lineIndex, accountId) => {
    const newMapping = { ...mapping };
    newMapping[section][lineIndex].accounts = newMapping[section][lineIndex].accounts.filter(a => a !== accountId);
    setMapping(newMapping);
  };

  const removeLine = (section, index) => {
    const newMapping = { ...mapping };
    newMapping[section].splice(index, 1);
    setMapping(newMapping);
  };

  const addLine = (section) => {
    const newMapping = { ...mapping };
    newMapping[section].push({ label: 'New Line Item', accounts: [] });
    setMapping(newMapping);
  };

  const addAccountToLine = (section, lineIndex, accountId) => {
    const newMapping = { ...mapping };
    if (!newMapping[section][lineIndex].accounts.includes(accountId)) {
      newMapping[section][lineIndex].accounts.push(accountId);
      setMapping(newMapping);
    }
  };

  const handleDragStart = (e, account) => {
    setDraggedAccount(account);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setDraggedAccount(null);
    setDragOverLine(null);
  };

  const handleDragOver = (e, section, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverLine({ section, index });
  };

  const handleDragLeave = () => {
    setDragOverLine(null);
  };

  const handleDrop = (e, section, index) => {
    e.preventDefault();
    if (draggedAccount) {
      // Validate that the account type is allowed in this section
      if (!canDropInSection(draggedAccount.type, section)) {
        alert(`${draggedAccount.type} accounts cannot be mapped to ${sections.find(s => s.key === section)?.label || section}. Please drag to an appropriate section.`);
        setDraggedAccount(null);
        setDragOverLine(null);
        return;
      }
      addAccountToLine(section, index, draggedAccount.id);
    }
    setDraggedAccount(null);
    setDragOverLine(null);
  };

  // Line item reordering
  const [draggedLineIndex, setDraggedLineIndex] = useState(null);
  const [dragOverLineIndex, setDragOverLineIndex] = useState(null);

  const handleLineDragStart = (e, index) => {
    e.stopPropagation();
    setDraggedLineIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleLineDragEnd = () => {
    setDraggedLineIndex(null);
    setDragOverLineIndex(null);
  };

  const handleLineDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedLineIndex !== null && draggedLineIndex !== index) {
      setDragOverLineIndex(index);
    }
  };

  const handleLineDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedLineIndex !== null && draggedLineIndex !== targetIndex) {
      const newMapping = { ...mapping };
      const section = newMapping[activeSection];
      const [movedItem] = section.splice(draggedLineIndex, 1);
      section.splice(targetIndex, 0, movedItem);
      setMapping(newMapping);
    }
    setDraggedLineIndex(null);
    setDragOverLineIndex(null);
  };

  const moveLineUp = (section, index) => {
    if (index === 0) return;
    const newMapping = { ...mapping };
    const temp = newMapping[section][index];
    newMapping[section][index] = newMapping[section][index - 1];
    newMapping[section][index - 1] = temp;
    setMapping(newMapping);
  };

  const moveLineDown = (section, index) => {
    if (index >= mapping[section].length - 1) return;
    const newMapping = { ...mapping };
    const temp = newMapping[section][index];
    newMapping[section][index] = newMapping[section][index + 1];
    newMapping[section][index + 1] = temp;
    setMapping(newMapping);
  };

  const getAllMapped = () => {
    const mapped = [];
    Object.values(mapping).forEach(section => {
      section.forEach(line => {
        mapped.push(...line.accounts);
      });
    });
    return mapped;
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await mappingsAPI.save(clientId, mapping);
    } catch (err) {
      console.error('Failed to save mapping:', err);
    }
    setLoading(false);
    onComplete(mapping);
  };

  const allMapped = getAllMapped();

  return (
    <div data-testid="remap-layer" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <Tag>Step 2 of 3</Tag>
        <h2 className="font-heading" style={{ fontSize: 28, fontWeight: 500, color: COLORS.dark, marginTop: 12 }}>
          Map Chart of Accounts
        </h2>
        <p style={{ color: COLORS.muted, marginTop: 8 }}>
          Assign QuickBooks accounts to presentation line items for financial statements.
        </p>
      </div>

      {aiMapping && (
        <div
          data-testid="auto-ai-map-banner"
          style={{
            marginBottom: 24,
            padding: '14px 18px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(99,102,241,0.08))',
            border: '1px solid rgba(99,102,241,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: '#4338CA',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <Loader2 size={16} className="animate-spin" />
          <Sparkles size={16} />
          AI is mapping your QuickBooks accounts… you'll be able to refine the groupings in a moment.
        </div>
      )}

      {/* Template Picker */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Template:</span>
        {Object.keys(DEFAULT_TEMPLATES).map(t => (
          <button
            key={t}
            onClick={() => handleTemplateChange(t)}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              border: `2px solid ${selectedTemplate === t ? COLORS.primary : COLORS.border}`,
              background: selectedTemplate === t ? COLORS.primary : COLORS.card,
              color: selectedTemplate === t ? '#fff' : COLORS.muted,
              cursor: 'pointer',
            }}
          >
            {t}
          </button>
        ))}
        <button style={{
          padding: '7px 16px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          border: `2px dashed ${COLORS.border}`,
          background: COLORS.card,
          color: COLORS.muted,
          cursor: 'pointer',
        }}>
          + New Template
        </button>
        
        {/* AI Auto-Map Button */}
        <button
          onClick={() => handleAIMapping(false)}
          disabled={aiMapping || accounts.length === 0}
          style={{
            marginLeft: 'auto',
            padding: '8px 20px',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            border: 'none',
            background: aiMapping ? COLORS.muted : 'linear-gradient(135deg, #8B5CF6, #6366F1)',
            color: '#fff',
            cursor: aiMapping ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
          }}
        >
          {aiMapping ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Mapping...
            </>
          ) : (
            <>
              <Sparkles size={14} />
              AI Auto-Map
            </>
          )}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24 }}>
        <div>
          {/* Section Tabs - Grouped by Category */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Profit & Loss
            </div>
            <div style={{ display: 'flex', gap: 2, background: COLORS.off, borderRadius: 10, padding: 4, marginBottom: 12 }}>
              {sections.filter(s => s.category === 'P&L').map(sec => (
                <button
                  key={sec.key}
                  onClick={() => setActiveSection(sec.key)}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    border: 'none',
                    background: activeSection === sec.key ? COLORS.card : 'transparent',
                    color: activeSection === sec.key ? COLORS.dark : COLORS.muted,
                    cursor: 'pointer',
                    boxShadow: activeSection === sec.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  {sec.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Balance Sheet
            </div>
            <div style={{ display: 'flex', gap: 2, background: COLORS.off, borderRadius: 10, padding: 4 }}>
              {sections.filter(s => s.category === 'Balance Sheet').map(sec => (
                <button
                  key={sec.key}
                  onClick={() => setActiveSection(sec.key)}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    border: 'none',
                    background: activeSection === sec.key ? COLORS.card : 'transparent',
                    color: activeSection === sec.key ? COLORS.dark : COLORS.muted,
                    cursor: 'pointer',
                    boxShadow: activeSection === sec.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  {sec.label}
                </button>
              ))}
            </div>
          </div>

          {/* Line Items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(mapping[activeSection] || []).map((line, idx) => {
              const lineTotal = sumAccounts(accounts, line.accounts);
              const isEditing = editingLine?.section === activeSection && editingLine?.index === idx;
              const sectionColor = sections.find(s => s.key === activeSection)?.color;
              const isDragOver = dragOverLine?.section === activeSection && dragOverLine?.index === idx;
              
              return (
                <Card 
                  key={idx} 
                  draggable
                  onDragStart={(e) => handleLineDragStart(e, idx)}
                  onDragEnd={handleLineDragEnd}
                  onDragOver={(e) => {
                    handleLineDragOver(e, idx);
                    if (!draggedLineIndex) handleDragOver(e, activeSection, idx);
                  }}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => {
                    if (draggedLineIndex !== null) {
                      handleLineDrop(e, idx);
                    } else {
                      handleDrop(e, activeSection, idx);
                    }
                  }}
                  style={{ 
                    padding: 16, 
                    borderLeft: `4px solid ${sectionColor}`,
                    border: isDragOver ? `2px dashed ${COLORS.primary}` : dragOverLineIndex === idx ? `2px dashed ${COLORS.success}` : undefined,
                    background: isDragOver ? COLORS.primaryLight : dragOverLineIndex === idx ? '#F0FDF4' : draggedLineIndex === idx ? COLORS.off : COLORS.card,
                    transition: 'all 0.15s ease',
                    cursor: 'grab',
                    opacity: draggedLineIndex === idx ? 0.5 : 1,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: line.accounts.length > 0 ? 12 : 0 }}>
                    {/* Drag Handle */}
                    <div style={{ color: COLORS.muted, cursor: 'grab', padding: '4px 0' }} title="Drag to reorder">
                      <GripVertical size={16} />
                    </div>
                    {/* Reorder Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      <button 
                        onClick={() => moveLineUp(activeSection, idx)} 
                        disabled={idx === 0}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: idx === 0 ? 'not-allowed' : 'pointer', 
                          color: idx === 0 ? COLORS.border : COLORS.muted,
                          padding: 0,
                          lineHeight: 0,
                        }}
                        title="Move up"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button 
                        onClick={() => moveLineDown(activeSection, idx)} 
                        disabled={idx === (mapping[activeSection]?.length || 1) - 1}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: idx === (mapping[activeSection]?.length || 1) - 1 ? 'not-allowed' : 'pointer', 
                          color: idx === (mapping[activeSection]?.length || 1) - 1 ? COLORS.border : COLORS.muted,
                          padding: 0,
                          lineHeight: 0,
                        }}
                        title="Move down"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                    {isEditing ? (
                      <input
                        value={editLabel}
                        onChange={e => setEditLabel(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={e => e.key === 'Enter' && saveEdit()}
                        autoFocus
                        style={{
                          flex: 1,
                          padding: '4px 10px',
                          borderRadius: 6,
                          border: `2px solid ${COLORS.primary}`,
                          fontSize: 14,
                          fontWeight: 600,
                          color: COLORS.dark,
                        }}
                      />
                    ) : (
                      <span 
                        style={{ flex: 1, fontSize: 14, fontWeight: 600, color: COLORS.dark, cursor: 'pointer' }}
                        onDoubleClick={() => startEdit(activeSection, idx)}
                        title="Double-click to rename"
                      >
                        {line.label}
                      </span>
                    )}
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 13, color: lineTotal < 0 ? COLORS.error : COLORS.dark, fontWeight: 600 }}>
                      {formatCurrency(lineTotal)}
                    </span>
                    <button onClick={() => startEdit(activeSection, idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.muted }}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => removeLine(activeSection, idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.error }}>
                      <X size={14} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {line.accounts.map(accId => {
                      const acc = getAccountById(accounts, accId);
                      return acc ? (
                        <span key={accId} style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: COLORS.off,
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 11,
                          fontWeight: 500,
                          color: COLORS.dark,
                          border: `1px solid ${COLORS.border}`,
                        }}>
                          <span style={{ color: COLORS.muted, fontFamily: 'JetBrains Mono', fontSize: 10 }}>{acc.id}</span>
                          {acc.name}
                          <button 
                            onClick={() => removeAccount(activeSection, idx, accId)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.muted, padding: 0 }}
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ) : null;
                    })}
                    {line.accounts.length === 0 && (
                      <span style={{ color: COLORS.muted, fontSize: 12, fontStyle: 'italic' }}>No accounts assigned</span>
                    )}
                  </div>
                </Card>
              );
            })}
            <button
              onClick={() => addLine(activeSection)}
              style={{
                padding: 12,
                borderRadius: 10,
                border: `2px dashed ${COLORS.border}`,
                background: COLORS.card,
                color: COLORS.muted,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                textAlign: 'center',
              }}
            >
              <Plus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Add Line Item
            </button>
          </div>
        </div>

        {/* Account Pool */}
        <div>
          <Card style={{ position: 'sticky', top: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 4 }}>
              Account Pool
            </div>
            
            {/* Show which types are valid for current section */}
            <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 10 }}>
              Showing <strong style={{ color: COLORS.dark }}>{sections.find(s => s.key === activeSection)?.allowedTypes.join(', ')}</strong> accounts for {sections.find(s => s.key === activeSection)?.label}
            </div>
            
            {/* Filter accounts to only show allowed types for active section */}
            {(() => {
              const currentSection = sections.find(s => s.key === activeSection);
              const allowedTypes = currentSection?.allowedTypes || [];
              const filteredAccounts = accounts.filter(a => allowedTypes.includes(a.type));
              const unmappedFiltered = filteredAccounts.filter(a => !allMapped.includes(a.id));
              const mappedFiltered = filteredAccounts.filter(a => allMapped.includes(a.id));
              
              // Sort by balance (highest first) to show most significant accounts
              const sortedUnmapped = [...unmappedFiltered].sort((a, b) => Math.abs(b.balance || 0) - Math.abs(a.balance || 0));
              const sortedMapped = [...mappedFiltered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              
              return (
                <>
                  {/* Unmapped accounts first */}
                  {sortedUnmapped.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.warning, marginBottom: 6, textTransform: 'uppercase' }}>
                        Unmapped ({sortedUnmapped.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflowY: 'auto' }}>
                        {sortedUnmapped.map(a => {
                          const typeColors = getAccountTypeColor(a.type);
                          return (
                            <div
                              key={a.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, a)}
                              onDragEnd={handleDragEnd}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '8px 10px',
                                borderRadius: 6,
                                background: COLORS.card,
                                border: `2px solid ${COLORS.primary}`,
                                fontSize: 11,
                                cursor: 'grab',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = COLORS.primaryLight;
                                e.currentTarget.style.transform = 'translateX(4px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = COLORS.card;
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                              title={`${a.name} - Balance: ${formatCurrency(a.balance)}`}
                            >
                              <span style={{ flex: 1, color: COLORS.dark, fontWeight: 600 }}>{a.name}</span>
                              <span style={{ 
                                fontSize: 10, 
                                color: (a.balance || 0) >= 0 ? COLORS.success : COLORS.error,
                                fontWeight: 600,
                                fontFamily: 'JetBrains Mono, monospace',
                              }}>
                                {formatCurrency(a.balance)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Already mapped accounts */}
                  {sortedMapped.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.success, marginBottom: 6, textTransform: 'uppercase' }}>
                        Already Mapped ({sortedMapped.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 120, overflowY: 'auto' }}>
                        {sortedMapped.map(a => (
                          <div
                            key={a.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '5px 8px',
                              borderRadius: 5,
                              background: '#F0FDF4',
                              border: '1px solid #BBF7D0',
                              fontSize: 10,
                              opacity: 0.7,
                            }}
                          >
                            <Check size={10} color={COLORS.success} />
                            <span style={{ flex: 1, color: COLORS.muted }}>{a.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {filteredAccounts.length === 0 && (
                    <div style={{ padding: 20, textAlign: 'center', color: COLORS.muted, fontSize: 12 }}>
                      No {allowedTypes.join('/')} accounts found
                    </div>
                  )}
                </>
              );
            })()}
            
            {/* Progress */}
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: COLORS.muted }}>Total Mapped</span>
                <span style={{ color: COLORS.success, fontWeight: 600 }}>{allMapped.length} / {accounts.length}</span>
              </div>
              <div style={{ height: 5, background: COLORS.off, borderRadius: 4, marginTop: 5, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(allMapped.length / accounts.length) * 100}%`, background: COLORS.primary, borderRadius: 4 }} />
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          data-testid="remap-generate-btn"
          onClick={handleComplete}
          disabled={loading}
          style={{
            padding: '12px 32px',
            borderRadius: 8,
            background: loading ? COLORS.muted : COLORS.primary,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {loading && <Loader2 size={18} className="animate-spin" />}
          Generate Report <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
//   REPORT LAYER
// ═══════════════════════════════════════════════════
function ReportLayer({ accounts, mapping, client, trendData, ttmData, ttmLoading = false, ttmError = null, onRetryTtm = null, isPortal = false, qboMeta = null,
                       dashboardPeriod = { id: 'prior_month' }, onPeriodChange = null, periodLoading = false, periodError = null }) {
  const [activeReport, setActiveReport] = useState('pl');
  // V16 Pass D: tracks the in-flight server-side PDF generation request
  // so the Export PDF button can render a loading state and disable
  // double-submits.
  const [exportingPdf, setExportingPdf] = useState(false);
  // V25 — validation coverage / gate result for the last package generation
  const [packageResult, setPackageResult] = useState(null);
  // V19.3 — Send-to-Client in-flight flag (gates double-clicks while
  // the server renders + emails).
  const [sendingToClient, setSendingToClient] = useState(false);
  // V18 Step 2.5 — Unsaved-mapping-changes warning on Export PDF.
  // Mirrors the same check inside PrintModal for the in-modal Print
  // button. Holds { onSaveAndContinue, onContinueAnyway, onCancel } or null.
  const [unsavedExportWarning, setUnsavedExportWarning] = useState(null);
  // V18 Step 2.5 — Stable signature of a mapping object for change
  // detection. Compares section → ordered (label, sorted-account-ids)
  // tuples; ignores volatile fields like computed balances.
  const mappingSignature = React.useCallback((m) => {
    if (!m || typeof m !== 'object') return '';
    const sections = Object.keys(m).sort();
    const parts = [];
    sections.forEach((s) => {
      const arr = m[s];
      if (!Array.isArray(arr)) return;
      arr.forEach((line) => {
        const ids = Array.isArray(line?.accounts) ? [...line.accounts].sort().join(',') : '';
        parts.push(`${s}|${line?.label || ''}|${ids}`);
      });
    });
    return parts.join('\n');
  }, []);
  // V15.2: period labels now derive from qboMeta (period_start / period_end)
  // when available. Fallback is end-of-last-completed-month for the legacy
  // demo/no-sync path. Cover page descriptor reads "Four Months Ended
  // April 30, 2026" instead of just "Through April 30, 2026" so single-
  // month vs YTD reports are visually distinguished.
  const periodLabels = defaultPeriodLabels(qboMeta);
  const periodLabel = (() => {
    const d = periodLabels.periodEnd;
    return `${['January','February','March','April','May','June','July','August','September','October','November','December'][d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  })();
  const period = periodLabels.monthsEndedLabel;
  const reportingYear = periodLabels.reportingYear;
  const priorYear = periodLabels.priorYear;
  const balanceSheetDate = periodLabels.asOfLabel;
  const ytdLabel = periodLabels.ytdLabel;
  const priorYtdLabel = periodLabels.priorYtdLabel;

  // Financial calculations
  const sum = (ids) => sumAccounts(accounts, ids);
  const sumPrior = (ids) => sumAccountsPrior(accounts, ids);
  const getAcc = (id) => getAccountById(accounts, id);

  // Section display signs — backend returns natural debit-credit signed balances.
  // Credit-natural sections (revenue, liabilities, equity, other income) flip the sign
  // so the report shows positive numbers in standard CPA presentation.
  const SIGN = {
    income: -1, cogs: +1, opex: +1, other_income: -1, other_expense: +1,
    cash: +1, assets_current: +1, assets_fixed: +1, assets_other: +1,
    liabilities_current: -1, debt_short: -1, liabilities_long: -1, equity: -1,
  };

  // Sum a mapping section with natural display sign applied
  const sumSection = (sectionKey, prior = false) => {
    if (!mapping || !mapping[sectionKey]) return 0;
    const sign = SIGN[sectionKey] ?? 1;
    const accSum = prior ? sumPrior : sum;
    return sign * mapping[sectionKey].reduce(
      (total, line) => total + accSum(line.accounts || []), 0
    );
  };
  // Sum a single line item with the section's natural display sign applied
  const lineVal = (sectionKey, line, prior = false) => {
    const sign = SIGN[sectionKey] ?? 1;
    const accSum = prior ? sumPrior : sum;
    return sign * accSum(line.accounts || []);
  };

  // Current year (YTD through last completed month)
  const totalRevenue = sumSection('income');
  const totalCOGS    = sumSection('cogs');
  const grossProfit  = totalRevenue - totalCOGS;
  const totalOpEx    = sumSection('opex');
  const otherIncome  = sumSection('other_income');
  const otherExpense = sumSection('other_expense');
  // Legacy fallback if user has unsplit 'other' bucket — treat as net (positive = income)
  const legacyOtherNet = (!otherIncome && !otherExpense && mapping?.other)
    ? mapping.other.reduce((s, l) => s + sum(l.accounts || []), 0)
    : 0;
  const ebit = grossProfit - totalOpEx;
  const netIncome = ebit + otherIncome - otherExpense + legacyOtherNet;
  // Kept for any legacy UI that references them
  const interest = 0;
  const ebt = netIncome;
  const taxes = 0;

  // Prior year (same period, prior year) — pulled from QBO prior-period Trial Balance
  const pyRevenue = sumSection('income', true);
  const pyCOGS    = sumSection('cogs', true);
  const pyGross   = pyRevenue - pyCOGS;
  const pyOpEx    = sumSection('opex', true);
  const pyOtherI  = sumSection('other_income', true);
  const pyOtherE  = sumSection('other_expense', true);
  const pyEBIT    = pyGross - pyOpEx;
  const pyNetIncome = pyEBIT + pyOtherI - pyOtherE;
  const hasPriorYearData = accounts.some(a => Math.abs(a.prior_balance || 0) > 0.005);

  // Legacy line items kept for the per-line PLRow rendering (best-effort)
  const compensation = sum(['6000', '6010', '6020', '6030']);
  const occupancy = sum(['6100', '6110', '6120']);
  const marketing = sum(['6200', '6210']);
  const insurance = getAcc('6300')?.balance || 0;
  const professional = getAcc('6310')?.balance || 0;
  const depreciation = getAcc('6400')?.balance || 0;
  const gaOther = sum(['6500', '6510', '6520']);

  // Build canonical structured BS once — gives us `items` (group + leaf) per
  // section. Renderer below walks these items so Section → Group → Leaf
  // structure is consistent across BS, BS PY, IS, IS PY, T12, CF.
  // Pass current AND prior NI into BS so the PY equity tie-up adjustment
  // lands on the largest PY equity line — keeps the bs_balance_py tie
  // green (was failing by $1 due to sub-cent accumulation in PY rounding).
  const isCanonical = buildIncomeStatement(accounts, mapping, {
    plPeriod: qboMeta?.pl_period,
    plPeriodPrior: qboMeta?.pl_period_prior,
  });
  // V25.16 — the reporting month on its own, for the MONTH column beside the
  // year-to-date figures on the Statement of Income. Same builder, same
  // mapping, month-scoped P&L. Null when QBO did not return the month pull.
  const isMonthCanonical = qboMeta?.pl_month
    ? buildIncomeStatement(accounts, mapping, {
        plPeriod: qboMeta.pl_month,
        plPeriodPrior: qboMeta.pl_month_prior,
      })
    : null;
  const bsCanonical = buildBalanceSheet(
    accounts, mapping,
    bsNetIncomeAnchor(isCanonical.totals.netIncome, qboMeta),
    isCanonical.prior.netIncome,
  );

  // Balance Sheet from user's mapping with natural display signs
  const cash = sumSection('cash');
  const otherCurrentAssets = sumSection('assets_current');
  const totalCA = cash + otherCurrentAssets;
  const ar = 0;
  const inventory = 0;
  const totalFA = sumSection('assets_fixed');
  const otherAssets = sumSection('assets_other');
  const equipNet = sum(['1500', '1510']);
  const bldgNet = sum(['1600', '1610']);
  const totalAssets = totalCA + totalFA + otherAssets;

  const totalCL = sumSection('liabilities_current');
  const ap = 0;
  const accruedPay = 0;
  const accruedLib = 0;
  const shortTermDebt = sumSection('debt_short');
  const loc = 0;
  const totalLTD = sumSection('liabilities_long');
  const noteEquip = 0;
  const noteBldg = 0;
  const totalLiab = totalCL + shortTermDebt + totalLTD;
  const equitySum = sumSection('equity');
  const stock = 0;
  const retEarnings = equitySum + netIncome;
  // Current-period net income flows into equity (closing entry). Note: if the saved
  // mapping already includes a "Retained Earnings — Current Year" line sourced from QBO,
  // adding netIncome on top would double-count; we keep this for now to match the
  // displayed "Net Income (current year)" row on the BS — user can adjust mapping if needed.
  const totalEquity = equitySum + netIncome;
  const totalLE = totalLiab + totalEquity;

  // Get equity labels based on entity type
  const getEquityLabels = (entityType) => {
    switch(entityType) {
      case 's_corp':
      case 'c_corp':
        return { equity: "STOCKHOLDERS' EQUITY", stock: 'Common Stock', retained: 'Retained Earnings' };
      case 'sole_prop':
        return { equity: "OWNER'S EQUITY", stock: "Owner's Capital", retained: "Owner's Draws" };
      case 'nonprofit':
        return { equity: 'NET ASSETS', stock: 'Net Assets Without Donor Restrictions',
                 retained: 'Change in Net Assets' };
      case 'smllc':
      case 'multi_llc':
      case 'llc':
      default:
        return { equity: "MEMBERS' EQUITY", stock: "Members' Capital", retained: "Members' Draws & Distributions" };
    }
  };
  const equityLabels = getEquityLabels(client?.entity_type);

  // V25 — single source of truth for the renderer's row model. Both
  // "Export PDF" and "Send to Client" go through this, so the emailed
  // package is the same gated output the staff member downloaded.
  const buildV25Payload = () => {
    const cfForPdf = buildCashFlow(accounts, mapping, netIncome, pyNetIncome, {
      materialityThreshold: client?.cf_materiality_threshold,
      totalAssets: bsCanonical.totals.totalAssets,
      niAtPeriodStart: qboMeta?.ni_at_period_start,
      plPeriod: qboMeta?.pl_period,
      niAtPeriodEnd: netIncome,
      cashAtPeriodStart: qboMeta?.cash_at_period_start,
    });
    const ttmMonthLabels = (ttmData?.months || [])
      .map((m) => `${m.month} ${String(m.year).slice(-2)}`);
    const t12ForPdf = ttmData
      ? (ttmData.detail_lines_monthly
          ? buildT12(accounts, mapping, ttmData, ttmMonthLabels)
          : buildT12(ttmData?.accounts_monthly || [], mapping, ttmMonthLabels))
      : null;
    return buildPackagePayload({
      client,
      bs: bsCanonical,
      is: isCanonical,
      isMonth: isMonthCanonical,
      cf: cfForPdf,
      t12: t12ForPdf,
      months: ttmMonthLabels,
      periodLabels,
    });
  };


  // V25 — one issuance path for both Print and Export PDF. The server runs the
  // blocking gate, renders, re-foots the finished PDF and archives it; nothing
  // reaches the user unless it foots.
  const issueV25Package = async (mode = 'download') => {
    if (!qboMeta?.period_end) {
      setPackageResult({
        ok: false,
        report: 'The engagement period has not loaded from QuickBooks.\n\n'
          + '  The reporting period end date is required before a package can\n'
          + '  be issued. If this persists, the QuickBooks connection needs to\n'
          + '  be re-authorised on the Import step.\n\nStatement not issued.\n\n'
          + `  Renderer V${EMITTER_VERSION}`,
      });
      return;
    }
    try {
      setExportingPdf(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/render-package`,
        { client_id: client.id, period_end: qboMeta.period_end, payload: buildV25Payload() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('smj_token')}` },
          timeout: 120000 },
      );
      const data = resp.data;
      const bytes = Uint8Array.from(atob(data.pdf_base64), (ch) => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      let opened = null;
      if (mode === 'print') opened = window.open(url, '_blank');
      if (!opened) {
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setPackageResult({ ok: true, report: data.coverage_report,
                         warnings: data.warnings || [], pages: data.pages });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPackageResult({
        ok: false,
        report: (detail && typeof detail === 'object' && detail.report)
          ? detail.report
          : (typeof detail === 'string' ? detail
             : 'PDF generation failed. See the browser console for details.'),
      });
      console.error('V25 issuance error:', err, detail);
    } finally {
      setExportingPdf(false);
    }
  };

  const kpis = [
    { label: 'Total Revenue', value: formatCurrency(totalRevenue, true), sub: period, color: COLORS.primary },
    { label: 'Gross Profit', value: formatCurrency(grossProfit, true), sub: `${formatPercent(grossProfit, totalRevenue)} margin`, color: COLORS.success },
    { label: 'Net Income', value: formatCurrency(netIncome, true), sub: `${formatPercent(netIncome, totalRevenue)} net margin`, color: netIncome > 0 ? COLORS.success : COLORS.error },
    { label: 'Total Assets', value: formatCurrency(totalAssets, true), sub: 'Balance Sheet', color: COLORS.primary },
  ];

  // Prior year data (simulated as 85% of current year for demo)
  const priorYearFactor = 0.85;
  const pyTotalRevenue = totalRevenue * priorYearFactor;

  // Variance % vs prior year, with classification-aware coloring
  const variancePct = (curr, prior) => {
    if (!hasPriorYearData || prior === undefined || prior === null) return null;
    if (Math.abs(prior) < 0.005) return null;
    return ((curr - prior) / Math.abs(prior)) * 100;
  };

  // Trailing 12-month data — prefer real QBO TTM, fall back to legacy trendData
  const chartData = ttmData?.months?.map(m => ({
    month: m.month,
    revenue: m.revenue,
    netIncome: m.net_income,
  })) || trendData;

  // Live validation result — feeds the banner AND gates the publish button.
  // Per-statement canonical flags:
  //   - cashFlowCanonical:    auto-lifts when both CF ties pass
  //                           (cf_ni_ties_to_is + cf_ending_cash_ties_to_bs)
  //   - bsPriorYearCanonical: true  (PrintModal BS PY rewritten on canonical lib)
  //   - isPriorYearCanonical: true  (PrintModal IS PY rewritten on canonical lib)
  //   - t12Canonical:         true  (per-account monthly drilldown wired)
  // V25.21 — see isPeriodUnsettled in lib/reports.js: nothing turns red until
  // the accounts pull and the T12 pull on hand describe the SAME period.
  const periodUnsettled = isPeriodUnsettled({
    periodLoading, ttmLoading, qboMeta, ttmData, ttmError,
    selectedPeriod: dashboardPeriod,
  });

  const validationResult = React.useMemo(
    () => runValidationTies(accounts, mapping, ttmData, {
      bsPriorYearCanonical: true,
      isPriorYearCanonical: true,
      t12Canonical: true,
      qboReconciliation: qboMeta?.reconciliation,
      qboCompleteness:   qboMeta?.completeness,
      // V15.1: period-aware CF requires opening-balance metadata.
      cashAtPeriodStart: qboMeta?.cash_at_period_start,
      // V22 — period-scoped QBO P&L drives the IS.
      plPeriod:      qboMeta?.pl_period,
      plPeriodPrior: qboMeta?.pl_period_prior,
      // V17 — surface qboMeta + engagement settings so the new tie family
      // (bs_py_period_matches_engagement, is_py_period_matches_engagement,
      // t12_period_matches_engagement, framework_consistency_check) can
      // evaluate the engagement-level configuration.
      qboMeta,
      bsPyComparisonBasis: client?.bs_py_comparison_basis || 'prior_year_same_date',
      framework: client?.framework || 'gaap',
      // V19 — pass engagement type so the framework_consistency_check
      // tie can skip on PREPARED engagements (where no framework is
      // claimed) and remain active on COMPILATION engagements.
      engagementType: client?.engagement_type || 'preparation',
      cashFlow: buildCashFlow(accounts, mapping, netIncome, pyNetIncome, {
        materialityThreshold: client?.cf_materiality_threshold,
        totalAssets: (
          sectionTotal(accounts, mapping, 'cash') +
          sectionTotal(accounts, mapping, 'assets_current') +
          sectionTotal(accounts, mapping, 'assets_fixed') +
          sectionTotal(accounts, mapping, 'assets_other')
        ),
        niAtPeriodStart: qboMeta?.ni_at_period_start,
        // V21 — period-scoped QBO P&L drives NI, D&A, interest, tax.
        plPeriod: qboMeta?.pl_period,
        // V21 — niAtPeriodEnd anchors the BS NetIncome row movement
        // formula for cross-FY periods; falls back to niExact otherwise.
        niAtPeriodEnd: netIncome,
        cashAtPeriodStart: qboMeta?.cash_at_period_start,
      }),
    }),
    [accounts, mapping, ttmData, qboMeta, client, netIncome, pyNetIncome]
  );

  // V19.16 — Advisories block deleted. The opening-RE niAtPS
  // reconciliation advisory was patching over a builder bug; with niAtPS
  // now read directly from QBO's BS NetIncome row (server.py, V19.16),
  // there are no two derived values to disagree, so no advisory is
  // possible or meaningful. Validation is binary: green or red.
  // CONTRIBUTING.md Rule 6 binds this.
  const validationAdvisories = [];

  const PLRow = ({ label, value, priorValue, bold, indent = 0, total, color, kind = 'income' }) => {
    const showPrior = hasPriorYearData && priorValue !== undefined;
    const v = variancePct(value, priorValue);
    // 'income' kind: increase=good (green). 'expense' kind: increase=bad (red).
    const varColor = v === null
      ? COLORS.muted
      : (kind === 'expense' ? (v > 0 ? COLORS.error : COLORS.success) : (v > 0 ? COLORS.success : COLORS.error));
    return (
      <tr style={{ borderBottom: total ? `2px solid ${COLORS.dark}` : 'none' }}>
        <td style={{ padding: '7px 0', paddingLeft: indent * 24, fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 400, color: color || COLORS.dark }}>{label}</td>
        <td style={{ padding: '7px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 400, color: color || (value < 0 ? COLORS.error : COLORS.dark) }}>
          {value !== undefined ? formatCurrency(value) : ''}
        </td>
        {showPrior && (
          <td className="col-aux-hide-mobile" style={{ padding: '7px 0 7px 12px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: bold ? 14 : 13, fontWeight: bold ? 700 : 400, color: priorValue < 0 ? COLORS.error : COLORS.muted }}>
            {priorValue !== undefined ? formatCurrency(priorValue) : ''}
          </td>
        )}
        {showPrior && (
          <td className="col-aux-hide-mobile" style={{ padding: '7px 0 7px 12px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: varColor, fontWeight: 600 }}>
            {v === null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`}
          </td>
        )}
        <td className="col-aux-hide-mobile" style={{ padding: '7px 0 7px 12px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.muted }}>
          {value !== undefined ? formatPercent(value, totalRevenue) : ''}
        </td>
      </tr>
    );
  };

  const BSRow = ({ label, value, priorValue, bold, indent = 0, total,
                   isGroupHeader = false, isGroupSubtotal = false,
                   'data-testid': dataTestId }) => {
    const showPrior = hasPriorYearData && priorValue !== undefined;
    // Group header: bold black, no italic, no gray. Group subtotal: bold,
    // light top border so the subtotal sits visually under the group leaves.
    const fw = (bold || isGroupHeader || isGroupSubtotal) ? 700 : 400;
    const fs = (bold || isGroupHeader || isGroupSubtotal) ? 13 : 12;
    const borderBottom = total
      ? `2px solid ${COLORS.dark}`
      : (isGroupSubtotal ? `1px solid ${COLORS.border}` : 'none');
    return (
      <tr
        data-testid={dataTestId}
        style={{ borderBottom, fontWeight: fw }}
      >
        <td style={{ padding: '6px 0', paddingLeft: indent * 20, fontSize: fs, fontWeight: fw, color: COLORS.dark }}>{label}</td>
        <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: fs, fontWeight: fw, color: (value !== undefined && value < 0) ? COLORS.error : COLORS.dark }}>
          {value !== undefined ? formatCurrency(value) : ''}
        </td>
        {showPrior && (
          <td className="col-aux-hide-mobile" style={{ padding: '6px 0 6px 12px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: fs, fontWeight: fw, color: priorValue < 0 ? COLORS.error : COLORS.muted }}>
            {priorValue !== undefined ? formatCurrency(priorValue) : ''}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div data-testid="report-layer" className="page-padding" style={{ maxWidth: activeReport === 'ttm' ? 1400 : 1000, margin: '0 auto', padding: '24px 32px' }} id="printable-report">
      {/* V25 — the legacy HTML PrintModal is retired here: Print and
          Export PDF both issue the gated ReportLab package. */}

      {/* V25 — Validation coverage / gate result. Spec Part 12.6: a check
          that silently does not run is worse than no check, so the coverage
          block is shown on every generation, pass or fail. */}
      {packageResult && (
        <div
          data-testid="package-result-overlay"
          onClick={() => setPackageResult(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,25,35,0.55)', zIndex: 120,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            overflowY: 'auto', padding: '48px 16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid={packageResult.ok ? 'package-coverage-panel' : 'package-gate-failure-panel'}
            style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720,
              padding: 26, boxShadow: '0 24px 60px rgba(15,25,35,0.28)',
              borderTop: `4px solid ${packageResult.ok ? COLORS.success : COLORS.error}`,
            }}
          >
            <h3 className="font-heading" style={{ fontSize: 19, fontWeight: 600, margin: '0 0 12px', color: COLORS.dark }}>
              {packageResult.ok
                ? `Package issued \u2014 ${packageResult.pages} pages`
                : 'Statement not issued'}
            </h3>
            <pre
              data-testid="package-coverage-report"
              style={{
                whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace',
                fontSize: 12, lineHeight: 1.6, color: packageResult.ok ? COLORS.dark : '#7F1D1D',
                background: packageResult.ok ? '#F7FAFC' : '#FEF2F2',
                border: `1px solid ${packageResult.ok ? COLORS.border : '#FECACA'}`,
                borderRadius: 8, padding: 14, margin: 0, maxHeight: '58vh', overflowY: 'auto',
              }}
            >{packageResult.report}</pre>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                data-testid="package-result-close-btn"
                onClick={() => setPackageResult(null)}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
                  background: '#fff', color: COLORS.dark, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* V18 Step 2.5 — Unsaved-mapping-changes warning for Export PDF.
          Mirrored from PrintModal so the user sees the same guard from
          either entry point. */}
      {unsavedExportWarning && (
        <div
          data-testid="unsaved-mapping-warning-export"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100, padding: 20,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%',
            maxWidth: 480, padding: 28,
            boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
              Unsaved mapping changes
            </div>
            <div style={{ fontSize: 13, color: COLORS.dark, lineHeight: 1.55, marginBottom: 22 }}>
              You have unsaved changes to the Chart of Accounts mapping.
              Save before exporting, or the PDF will reflect the last
              saved version on the server.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={unsavedExportWarning.onSaveAndContinue}
                data-testid="unsaved-export-save-and-continue-btn"
                style={{
                  padding: '11px 14px', borderRadius: 8, border: 'none',
                  background: COLORS.primary, color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >Save and continue</button>
              <button
                onClick={unsavedExportWarning.onContinueAnyway}
                data-testid="unsaved-export-continue-anyway-btn"
                style={{
                  padding: '11px 14px', borderRadius: 8,
                  border: `2px solid ${COLORS.border}`, background: '#fff',
                  color: COLORS.dark, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >Continue anyway (use saved version)</button>
              <button
                onClick={unsavedExportWarning.onCancel}
                data-testid="unsaved-export-cancel-btn"
                style={{
                  padding: '11px 14px', borderRadius: 8, border: 'none',
                  background: 'transparent', color: COLORS.muted,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="no-print report-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          {!isPortal && <Tag style={{ marginBottom: 12 }}>Step 3 of 3 — Report Preview</Tag>}
          <h2 className="font-heading" style={{ fontSize: 26, fontWeight: 500, color: COLORS.dark, margin: '0 0 4px' }}>
            {client?.company || 'Demo Company Inc.'}
          </h2>
          <p style={{ color: COLORS.muted, margin: 0, fontSize: 13 }}>{period}</p>
        </div>
        <div className="report-actions" style={{ display: 'flex', gap: 8 }}>
          {/* V15.2: PDF generation MUST be blocked when any tie is red.
              Previously failed ties disabled the Publish-to-Portal button
              but PDF/Print still produced packages containing broken
              statements. Gate them all the same way. */}
          {(() => {
            // V25.23 — issuance also waits for the period to SETTLE. The
            // figures on screen may still belong to the previously selected
            // window (a custom range mid-typing, or a T12 that has not caught
            // up), and those figures tie perfectly well — for the wrong
            // period. MEET Ministry printed an August package off a
            // January-to-July selection this way.
            const tiesPassing = validationResult.allGreen === true && !periodUnsettled;
            const tieDisabledStyle = tiesPassing ? {} : {
              opacity: 0.4, cursor: 'not-allowed', position: 'relative',
            };
            const tieDisabledTitle = tiesPassing
              ? ''
              : periodUnsettled
                ? 'Still loading the selected period — the figures on screen may belong to the previous window.'
                : `Cannot generate PDF — ${validationResult.ties.filter(t => !t.ok).length} validation tie(s) failing. Resolve before exporting.`;
            return (
          <>
          <button
            data-testid="print-btn"
            onClick={() => { if (tiesPassing) issueV25Package('print'); }}
            disabled={!tiesPassing}
            title={tieDisabledTitle}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: `2px solid ${COLORS.border}`,
              background: COLORS.card,
              color: COLORS.dark,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              ...tieDisabledStyle,
            }}
          >
            <Printer size={16} /> Print
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={async () => {
              if (!tiesPassing) return;
              // V21.2 (2026-06-11) — Gate on qboMeta.period_end being
              // loaded. The server 422-guard rejects period_end=null
              // requests. If the user clicks Export PDF before QBO
              // accounts have resolved (cold load, slow QBO), surface
              // a clear "data still loading" message instead of the
              // confusing "PDF rendering issue" the 422 would produce.
              if (!qboMeta?.period_end) {
                setPackageResult({
                  ok: false,
                  report: 'The engagement period has not loaded from QuickBooks.\n\n'
                    + '  The reporting period end date is required before a package can\n'
                    + '  be issued. If this persists, the QuickBooks connection needs to\n'
                    + '  be re-authorised on the Import step.\n\nStatement not issued.\n\n'
          + `  Renderer V${EMITTER_VERSION}`,
                });
                return;
              }
              // V18 Step 2.5 — Unsaved-mapping-changes guard. If the
              // dashboard's in-memory mapping differs from the persisted
              // server mapping, surface a 3-option modal before kicking
              // off the server render.
              const proceed = () => issueV25Package('download');
              // Check persisted vs in-memory mapping.
              let persisted = null;
              try {
                const r = await axios.get(
                  `${process.env.REACT_APP_BACKEND_URL}/api/mappings/${client.id}`,
                  { headers: { Authorization: `Bearer ${localStorage.getItem('smj_token')}` }, timeout: 8000 },
                );
                persisted = r?.data?.mapping || null;
              } catch (_) { /* fall through and proceed */ }
              const localSig = mappingSignature(mapping);
              const remoteSig = mappingSignature(persisted);
              if (localSig && remoteSig && localSig !== remoteSig) {
                setUnsavedExportWarning({
                  onSaveAndContinue: async () => {
                    setUnsavedExportWarning(null);
                    try {
                      await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/mappings/${client.id}`,
                        { mapping },
                        {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem('smj_token')}`,
                            'X-SMJ-Mapping-Origin': 'staff-coa-editor',
                            'Content-Type': 'application/json',
                          },
                          timeout: 10000,
                        },
                      );
                    } catch (_) {
                      alert('Could not save the mapping. The PDF will not be generated.');
                      return;
                    }
                    proceed();
                  },
                  onContinueAnyway: () => {
                    setUnsavedExportWarning(null);
                    proceed();
                  },
                  onCancel: () => setUnsavedExportWarning(null),
                });
                return;
              }
              proceed();
            }}
            disabled={!tiesPassing || exportingPdf}
            title={tieDisabledTitle}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: `2px solid ${COLORS.border}`,
              background: COLORS.card,
              color: COLORS.dark,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              ...tieDisabledStyle,
              ...(exportingPdf ? { opacity: 0.7, cursor: 'wait' } : {}),
            }}
          >
            <Download size={16} /> {exportingPdf ? 'Generating…' : 'Export PDF'}
          </button>
          </>
          ); })()}
          {!isPortal && (() => {
            // V19.3 — "Send to Client" button. Wires to
            // POST /api/clients/:id/send-to-client which reuses the
            // unified PDF cache, then emails the result via Resend
            // to client.contact_email.
            const sendToClient = async () => {
              if (sendingToClient) return;
              // NOTE (testing agent): `tiesPassing` is declared inside the
              // Print/Export IIFE above and is out of scope here — referencing
              // it threw `ReferenceError: tiesPassing is not defined` and put up
              // the red uncaught-runtime-error overlay on every Send click.
              // Re-derive it from the same source of truth.
              if (validationResult.allGreen !== true) {
                alert('Cannot send: validation ties are failing. Fix the red ties first.');
                return;
              }
              const recipientHint = client?.contact_email
                ? `Send the financial package to ${client.contact_email}?`
                : 'This client has no contact email on file. Add one in Client Settings first.';
              if (!client?.contact_email) {
                alert(recipientHint);
                return;
              }
              if (!qboMeta?.period_end) {
                setPackageResult({
                  ok: false,
                  report: 'The engagement period has not loaded from QuickBooks.\n\n'
                    + '  A package cannot be issued or emailed without the reporting\n'
                    + '  period end date.\n\nStatement not issued.\n\n'
          + `  Renderer V${EMITTER_VERSION}`,
                });
                return;
              }
              if (!window.confirm(recipientHint)) return;
              try {
                setSendingToClient(true);
                const effectiveReportType = client?.engagement_type === 'compilation' ? 'compiled' : 'prepared';
                const resp = await axios.post(
                  `${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/send-to-client`,
                  {
                    client_id: client.id,
                    package: 'package',
                    report_type: effectiveReportType,
                    period_end: qboMeta?.period_end,
                    // V25 — email the SAME gated ReportLab package the staff
                    // member downloads, not the legacy HTML render.
                    payload: buildV25Payload(),
                  },
                  {
                    headers: { Authorization: `Bearer ${localStorage.getItem('smj_token')}` },
                    timeout: 90000,
                  },
                );
                alert(`✅ Sent to ${resp.data.recipient}`);
              } catch (err) {
                const detail = err?.response?.data?.detail;
                if (detail && typeof detail === 'object' && detail.report) {
                  setPackageResult({ ok: false, report: detail.report });
                } else {
                  alert(`Send failed: ${detail || err?.message || 'Send failed.'}`);
                }
                console.error('Send to client error:', err);
              } finally {
                setSendingToClient(false);
              }
            };
            return (
              <button
                data-testid="send-to-portal-btn"
                onClick={sendToClient}
                disabled={sendingToClient}
                title={client?.contact_email
                  ? `Email PDF to ${client.contact_email}`
                  : 'No client contact email on file'}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: sendingToClient ? '#7FB8D8' : COLORS.primary,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: sendingToClient ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Send size={16} /> {sendingToClient ? 'Sending…' : 'Send to Client'}
              </button>
            );
          })()}
          {!isPortal && (() => {
            // V23.2 (2026-08-18) — Diagnostic export. Downloads a JSON
            // snapshot with EVERYTHING needed to root-cause a failing
            // validation tie WITHOUT round-tripping to the user:
            //   • client doc
            //   • full qboMeta (reconciliation, completeness,
            //     period_diagnostics, period_start_eve, ni_at_period_start,
            //     cash_at_period_start, pl_period[+prior], period_start,
            //     period_end)
            //   • accounts (all per-account balances current/prior/period_start)
            //   • mapping (canonical CoA groupings)
            //   • ttmData (T12 monthly rollup)
            //   • validationResult — the CRITICAL missing piece from the
            //     legacy diagnostic: per-tie {name, ok, detail, diagnostic}
            //     so we can SEE which tie is red without re-running the app.
            // Not gated on ties — the whole point is to export when ties fail.
            const downloadDiagnostic = () => {
              const nowIso = new Date().toISOString();
              const failingTies = (validationResult?.ties || []).filter((t) => !t.ok);
              const payload = {
                _meta: {
                  kind: 'SMJ Financial Portal — Diagnostic Export',
                  // V25.26 — stamps the live build, not a frozen literal, so a
                  // diagnostic always identifies the code that produced it.
                  version: `V${EMITTER_VERSION}`,
                  emitter_version: EMITTER_VERSION,
                  diagnostic_schema: 'V23.2',
                  disclaimer: 'DRAFT — DOES NOT TIE / NOT FOR ISSUANCE. Internal troubleshooting only; not for client delivery.',
                  generated_at: nowIso,
                  generated_by: (() => {
                    try {
                      const raw = localStorage.getItem('smj_user');
                      if (raw) return JSON.parse(raw).email || null;
                    } catch (_) { /* ignore */ }
                    return null;
                  })(),
                  environment: window.location.hostname,
                  dashboard_period: dashboardPeriod || null,
                  all_ties_green: validationResult?.allGreen === true,
                  failing_tie_count: failingTies.length,
                  failing_tie_names: failingTies.map((t) => t.key || t.name),
                },
                client: client || null,
                qbo_meta: qboMeta || null,
                validation: {
                  all_green: validationResult?.allGreen === true,
                  ties: (validationResult?.ties || []).map((t) => ({
                    key: t.key,
                    name: t.name,
                    label: t.label || null,
                    ok: t.ok,
                    detail: t.detail,
                    variance: t.variance ?? null,
                    diagnostic: t.diagnostic || null,
                    subchecks: t.subchecks || null,
                    // V25.16 — the period ties carry their evidence in
                    // `periodChecks`; without it a failure such as
                    // t12_period_matches_engagement exported as `{ok:false}`
                    // with nothing to read.
                    period_checks: t.periodChecks || null,
                    computed: t.computed || null,
                  })),
                  advisories: validationAdvisories || [],
                },
                ttm: ttmData || null,
                ttm_error: ttmError || null,
                accounts: accounts || [],
                mapping: mapping || null,
              };
              const slug = (client?.company || client?.name || 'client')
                .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
              const stamp = nowIso.replace(/[:.]/g, '-').replace(/T/, '_').slice(0, 19);
              const blob = new Blob(
                [JSON.stringify(payload, null, 2)],
                { type: 'application/json' },
              );
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `diagnostic_${slug}_${stamp}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              // Revoke on next tick so Safari has a chance to consume the URL.
              setTimeout(() => URL.revokeObjectURL(url), 500);
            };
            const failingCount = (validationResult?.ties || []).filter((t) => !t.ok).length;
            const btnTitle = failingCount > 0
              ? `Download diagnostic JSON — ${failingCount} tie(s) failing. Send this file to your developer.`
              : 'Download diagnostic JSON — all ties green (still useful for support).';
            return (
              <button
                data-testid="download-diagnostic-btn"
                onClick={downloadDiagnostic}
                title={btnTitle}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: `2px solid ${failingCount > 0 ? '#c02828' : COLORS.border}`,
                  background: failingCount > 0 ? '#fff5f5' : COLORS.card,
                  color: failingCount > 0 ? '#c02828' : COLORS.dark,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Bug size={16} /> Download Diagnostic{failingCount > 0 ? ` (${failingCount})` : ''}
              </button>
            );
          })()}
        </div>
      </div>

      {/* Print-only Header with Company Info */}
      <div className="print-header" style={{ display: 'none', textAlign: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '2px solid #0082B0' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0082B0', marginBottom: 4 }}>STEELE MARTIN JONES</div>
        <div style={{ fontSize: 10, color: '#666', letterSpacing: '0.1em', marginBottom: 16 }}>CPAs and Business Advisors</div>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#000' }}>{client?.company || 'Demo Company Inc.'}</div>
        <div style={{ fontSize: 12, color: '#666' }}>{period}</div>
      </div>

      {/* KPI Strip */}
      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        {kpis.map(k => (
          <Card key={k.label} className="kpi-card" style={{ padding: 20, borderTop: `4px solid ${k.color}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 8 }}>{k.label}</div>
            <div className="font-heading" style={{ fontSize: 26, fontWeight: 500, color: COLORS.dark, marginBottom: 4 }}>{k.value}</div>
            <div style={{ fontSize: 11, color: COLORS.muted }}>{k.sub}</div>
          </Card>
        ))}
      </div>

      {/* Trend Chart - Hidden on print */}
      <Card className="no-print trend-chart-container" style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: 16 }}>
          Revenue & Net Income — {reportingYear}
        </div>
        <ResponsiveContainer width="100%" height={190}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.muted }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 10, fill: COLORS.muted }} axisLine={false} tickLine={false} width={52} />
            <Tooltip 
              formatter={(v, n) => [formatCurrency(v), n === 'revenue' ? 'Revenue' : 'Net Income']}
              contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 12 }}
            />
            <Line type="monotone" dataKey="revenue" stroke={COLORS.primary} strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="netIncome" stroke={COLORS.success} strokeWidth={2} dot={false} strokeDasharray="4 3" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Validation Banner — release gate for publishing to client portal.
          V23 — while a period switch is in flight, suppress the banner
          entirely. Without this, the panel renders red against the
          stale period's data for ~200-400ms before the new data lands,
          which staff perceived as a transient error. The new period's
          banner repaints as soon as the fetch completes. */}
      {/* V25.7 — Data Completeness. Reads the TTM payload already in memory;
          no extra QBO calls. Non-blocking: it names the months QuickBooks
          returned empty so a package is never issued in the dark. */}
      {!isPortal && !periodUnsettled && !ttmLoading && ttmError && (
        <div
          data-testid="ttm-fetch-error-banner"
          style={{
            margin: '12px 0', padding: '14px 18px',
            background: '#FEF2F2', borderRadius: 10,
            border: '1px solid #FCA5A5', fontSize: 13, color: '#7F1D1D',
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}
        >
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Trailing 12 Months could not be loaded from QuickBooks
            </div>
            <div style={{ lineHeight: 1.5 }}>
              The T12 statement has no data, so the package cannot be issued
              (the <code>t12_period_matches_engagement</code> check has nothing
              to verify). QuickBooks returned
              {' '}{ttmError.status ? `HTTP ${ttmError.status}` : 'no response'}
              {ttmError.detail ? `: ${String(ttmError.detail).slice(0, 220)}` : ''}.
              {ttmError.status === 424 || ttmError.status === 401
                ? ' Reconnect QuickBooks on the Import step, then retry.'
                : ' Retry the pull — Intuit rate-limits and times out under load.'}
            </div>
          </div>
          {onRetryTtm && (
            <button
              data-testid="retry-ttm-btn"
              onClick={onRetryTtm}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid #B91C1C',
                background: '#fff', color: '#B91C1C', fontSize: 12,
                fontWeight: 700, cursor: 'pointer', flexShrink: 0,
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}
      {/* V25.20 — a QuickBooks pull for this period failed. Reported on
          MEET Ministry twice: first the whole period came back empty (the
          Income Statement rendered as zeros and the only symptom was a T12
          variance), then Intuit throttled two of the ten pulls, which is why
          the same custom period printed on one click and refused on the next.
          Name it here, at the top, in a sentence. */}
      {!isPortal && !periodUnsettled && !ttmError && qboMeta && qboMeta.period_end
        && (() => {
        const failed = Object.entries(qboMeta.fetch_reports || {})
          .filter(([, v]) => v && v.ok === false);
        const noPl = !qboMeta.pl_period;
        if (!noPl && failed.length === 0) return null;
        const throttled = failed.some(([, v]) => v.status === 429
          || /ThrottleExceeded/i.test(String(v.error || '')));
        return (
        <div
          data-testid="period-data-error-banner"
          style={{
            margin: '12px 0', padding: '14px 18px',
            background: '#FEF2F2', borderRadius: 10,
            border: '1px solid #FCA5A5', fontSize: 13, color: '#7F1D1D',
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}
        >
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {throttled
                ? 'QuickBooks rate-limited this company while loading the period'
                : noPl
                  ? 'QuickBooks returned no profit & loss for this period'
                  : 'Part of this period did not load from QuickBooks'}
            </div>
            <div style={{ lineHeight: 1.5 }}>
              {noPl
                ? `Every figure on the Statement of Income for ${qboMeta.period_start} to ${qboMeta.period_end} is zero because the period pull came back empty — the package cannot be issued.`
                : `One or more figures for ${qboMeta.period_start} to ${qboMeta.period_end} are missing, so the package cannot be issued.`}
              {' '}
              {failed.map(([k, v]) => describeFetchFailure(k, v)).join('  ·  ')}
              {'. '}
              {throttled
                ? 'This clears on its own — re-run the period.'
                : 'Re-run the period; if it keeps failing, reconnect QuickBooks on the Import step.'}
            </div>
          </div>
          {onPeriodChange && dashboardPeriod && (
            <button
              data-testid="retry-period-btn"
              onClick={() => onPeriodChange({ ...dashboardPeriod })}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid #B91C1C',
                background: '#fff', color: '#B91C1C', fontSize: 12,
                fontWeight: 700, cursor: 'pointer', flexShrink: 0,
              }}
            >
              Re-run period
            </button>
          )}
        </div>
        );
      })()}
      {/* V25.21 — nothing turns red until the period's data has SETTLED.
          Changing periods refetches the accounts and the T12 independently,
          so for a couple of seconds the new window was being judged against
          the previous T12 and the banner flashed red before turning green.
          Red is now reserved for a real failure: while a fetch is in flight,
          or while the T12 on hand still belongs to a different period end,
          the banner says it is re-checking. */}
      {!isPortal && !periodUnsettled && (
        <DataCompletenessBanner
          analysis={analyzeMonthCompleteness(ttmData)}
          busy={periodLoading}
          onUsePeriodEnd={onPeriodChange ? (endIso) => onPeriodChange({
            id: 'custom',
            start: `${endIso.slice(0, 4)}-01-01`,
            end: endIso,
          }) : null}
        />
      )}
      {!isPortal && !periodUnsettled && <ValidationBanner result={validationResult} advisories={validationAdvisories} />}
      {!isPortal && periodUnsettled && (
        <div
          data-testid="validation-banner-loading"
          style={{
            margin: '12px 0', padding: '14px 18px',
            background: '#F1F5F9', borderRadius: 10,
            border: '1px solid #CBD5E1',
            fontSize: 13, color: '#475569',
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <div style={{
            width: 14, height: 14, borderRadius: '50%',
            border: '2px solid #CBD5E1', borderTopColor: '#475569',
            animation: 'spin 0.7s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          {dashboardPeriod?.id === 'custom' && !isUsableDashboardPeriod(dashboardPeriod)
            ? 'Enter a full start and end date to run this period.'
            : 'Recalculating validation ties for the selected period…'}
        </div>
      )}

      {/* Statement Tabs - Hidden on print */}
      {/* V20.1 — Dashboard period picker. Defaults to Prior Month (the
          backend's default snapshot). Switching options refetches QBO at
          the chosen period; every report on this layer reflects that
          window. Mirrors the Print modal's option set (Prior Month, YTD,
          Last Quarter, Last Year, Custom). Hidden on portal-side (clients
          always see most-recent close). */}
      {!isPortal && onPeriodChange && (() => {
        const presets = [
          { id: 'prior_month', label: 'Prior Month',
            range: () => {
              const now = new Date();
              const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              const e = new Date(now.getFullYear(), now.getMonth(), 0);
              return { start: null, end: null, label: s.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) };
            }},
          { id: 'ytd', label: 'Year to Date',
            range: () => {
              const now = new Date();
              return {
                start: `${now.getFullYear()}-01-01`,
                end:   now.toISOString().slice(0, 10),
                label: `Year to Date · ${now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
              };
            }},
          { id: 'last_quarter', label: 'Last Quarter',
            range: () => {
              const now = new Date();
              const currentQ = Math.floor(now.getMonth() / 3);
              let lastQYear = now.getFullYear();
              let lastQ = currentQ - 1;
              if (lastQ < 0) { lastQ = 3; lastQYear -= 1; }
              const s = new Date(lastQYear, lastQ * 3, 1);
              const e = new Date(lastQYear, lastQ * 3 + 3, 0);
              return {
                start: s.toISOString().slice(0, 10),
                end:   e.toISOString().slice(0, 10),
                label: `Q${lastQ + 1} ${lastQYear}`,
              };
            }},
          { id: 'last_year', label: 'Last Year',
            range: () => {
              const y = new Date().getFullYear() - 1;
              return {
                start: `${y}-01-01`,
                end:   `${y}-12-31`,
                label: `Year Ended December 31, ${y}`,
              };
            }},
          { id: 'custom', label: 'Custom' },
        ];
        const activeId = dashboardPeriod?.id || 'prior_month';
        return (
          <div className="no-print period-picker" data-testid="dashboard-period-picker" style={{
            display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18,
            padding: '10px 12px', background: COLORS.off, borderRadius: 10, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Period
            </span>
            {presets.map((p) => (
              <button
                key={p.id}
                data-testid={`dashboard-period-${p.id}`}
                onClick={() => {
                  if (p.id === 'custom') {
                    onPeriodChange({ id: 'custom', start: dashboardPeriod?.start || '', end: dashboardPeriod?.end || '' });
                    return;
                  }
                  const r = p.range();
                  onPeriodChange({ id: p.id, start: r.start, end: r.end, label: r.label });
                }}
                style={{
                  padding: '6px 12px', borderRadius: 6, border: 'none',
                  background: activeId === p.id ? COLORS.primary : 'transparent',
                  color: activeId === p.id ? '#fff' : COLORS.dark,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
            {activeId === 'custom' && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 8 }}>
                <input
                  type="date"
                  value={dashboardPeriod?.start || ''}
                  data-testid="dashboard-period-custom-start"
                  onChange={(e) => onPeriodChange({ ...dashboardPeriod, id: 'custom', start: e.target.value })}
                  style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${COLORS.border}`, fontSize: 12 }}
                />
                <span style={{ fontSize: 11, color: COLORS.muted }}>to</span>
                <input
                  type="date"
                  value={dashboardPeriod?.end || ''}
                  data-testid="dashboard-period-custom-end"
                  onChange={(e) => onPeriodChange({ ...dashboardPeriod, id: 'custom', end: e.target.value })}
                  style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${COLORS.border}`, fontSize: 12 }}
                />
              </div>
            )}
            {periodLoading && (
              <span data-testid="dashboard-period-loading" style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.primary, fontWeight: 600 }}>
                Loading…
              </span>
            )}
            {periodError && !periodLoading && (
              <span data-testid="dashboard-period-error" style={{ marginLeft: 'auto', fontSize: 11, color: '#C8322B', fontWeight: 600 }}>
                {periodError}
              </span>
            )}
            {!periodLoading && !periodError && qboMeta?.period_end && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.muted }}>
                Through {qboMeta.period_end}
              </span>
            )}
          </div>
        );
      })()}

      {/* V20.2 — Period-range advisory. Some non-FY-start ranges (Last
          Quarter Q2-Q4, custom mid-year, custom multi-year) produce a
          CF identity break because QBO's Trial Balance returns YTD-from-
          fiscal-year-start P&L values regardless of the supplied
          start_date. We surface this BEFORE the validation banner so
          staff understand that the variance is a known reporting-window
          limitation, not a math error. Ranges that math-tie exactly:
            • Prior Month (the engagement default)
            • Year to Date
            • Last Year (Jan 1 - Dec 31 of prior FY)
            • Custom: any range starting on Jan 1
          Ranges that may show variance: Last Quarter (Q2-Q4), custom
          mid-year ranges, full-year ranges spanning fixed-asset disposals. */}
      {/* V21 (Day 3 cutover) — orange period-warning banner deleted.
          V21 builder produces identity-tying cash flows for ANY period
          shape (FY-aligned, mid-month, Q4 prior-year, cross-FY, single
          day). The TB YTD-from-FY-start quirk no longer leaks into CF
          math — see /app/memory/V21_DAY2_DIVERGENCE_LOG.md for the
          24/24 acceptance matrix at exact $0.00 identity. */}

      {/* V19.2 — Stale-schedule cleanup. The Debt Schedule and Fixed
          Assets tabs previously showed hardcoded mock loans / assets
          (First National Bank, 2022 Ford F-150, etc.) regardless of
          the actual client's balances, which surfaced as stale info
          for clients like SMJ with $0 LT debt and no fixed assets.
          Tabs now hide automatically when the canonical balance for
          the underlying section is $0. Consolidation tab is hidden
          for non-consolidation-group clients (the standalone mock
          GT Farm consolidation will be rebuilt under V19.4 work). */}
      {(() => {
        const ltDebtTotal = Math.abs(bsCanonical.sections.liabLong?.total || 0)
                          + Math.abs(bsCanonical.sections.debtShort?.total || 0);
        const fixedAssetsTotal = Math.abs(bsCanonical.sections.assetsFix?.total || 0);
        const isConsolidationGroup = client?.is_consolidation_group === true;
        const tabs = [
          ['pl', 'Income Statement'],
          ['ttm', 'TTM P&L'],
          ['bs', 'Balance Sheet'],
          ['cf', 'Cash Flow'],
          ['kpis', 'KPIs'],
        ];
        if (ltDebtTotal >= 0.5) tabs.push(['debt', 'Debt Schedule']);
        if (fixedAssetsTotal >= 0.5) tabs.push(['assets', 'Fixed Assets']);
        if (isConsolidationGroup) tabs.push(['consolidation', 'Consolidation']);
        // If active report is hidden, fall back to first visible tab.
        if (!tabs.find(([k]) => k === activeReport) && tabs.length > 0) {
          setTimeout(() => setActiveReport(tabs[0][0]), 0);
        }
        return (
          <div className="no-print report-tabs" style={{ display: 'flex', gap: 2, marginBottom: 24, background: COLORS.off, borderRadius: 10, padding: 4, width: 'fit-content' }}>
            {tabs.map(([k, l]) => (
              <button
                key={k}
                data-testid={`report-tab-${k}`}
                onClick={() => setActiveReport(k)}
                style={{
                  padding: '9px 18px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  border: 'none',
                  background: activeReport === k ? COLORS.card : 'transparent',
                  color: activeReport === k ? COLORS.dark : COLORS.muted,
                  cursor: 'pointer',
                  boxShadow: activeReport === k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {l}
              </button>
            ))}
          </div>
        );
      })()}

      {/* Income Statement */}
      {activeReport === 'pl' && (
        <Card className="statement-card">
          <div className="statement-meta-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px', color: COLORS.dark }}>Income Statement</h3>
              <div className="statement-sub" style={{ fontSize: 12, color: COLORS.muted }}>{period}</div>
            </div>
            <div className="statement-meta-pills" style={{ display: 'flex', gap: 24 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>EBITDA</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, color: COLORS.dark }}>{formatCurrency(ebit + depreciation)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Net Margin</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, color: netIncome > 0 ? COLORS.success : COLORS.error }}>{formatPercent(netIncome, totalRevenue)}</div>
              </div>
            </div>
          </div>
          <div className="statement-table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Description</th>
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>{ytdLabel}</th>
                {hasPriorYearData && (
                  <th className="col-aux-hide-mobile" style={{ padding: '8px 0 8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>{priorYtdLabel}</th>
                )}
                {hasPriorYearData && (
                  <th className="col-aux-hide-mobile" style={{ padding: '8px 0 8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Var %</th>
                )}
                <th className="col-aux-hide-mobile" style={{ padding: '8px 0 8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>% Rev</th>
              </tr>
            </thead>
            <tbody>
              <PLRow label="REVENUE" bold color={COLORS.muted} />
              {(mapping?.income || []).map((line, i) => {
                const val = lineVal('income', line);
                const pyVal = lineVal('income', line, true);
                if (Math.abs(val) < 0.005 && Math.abs(pyVal) < 0.005) return null;
                return <PLRow key={`inc-${i}`} kind="income" label={line.label} value={val} priorValue={pyVal} indent={1} />;
              })}
              <PLRow label="Total Revenue" value={totalRevenue} priorValue={pyRevenue} kind="income" bold total />
              <PLRow label="" />
              {(totalCOGS > 0 || pyCOGS > 0) && (
                <>
                  <PLRow label="COST OF REVENUE" bold color={COLORS.muted} />
                  {(mapping?.cogs || []).map((line, i) => {
                    const val = lineVal('cogs', line);
                    const pyVal = lineVal('cogs', line, true);
                    if (Math.abs(val) < 0.005 && Math.abs(pyVal) < 0.005) return null;
                    return <PLRow key={`cogs-${i}`} kind="expense" label={line.label} value={val} priorValue={pyVal} indent={1} />;
                  })}
                  <PLRow label="Total Cost of Revenue" value={totalCOGS} priorValue={pyCOGS} kind="expense" bold total />
                </>
              )}
              <PLRow label="GROSS PROFIT" value={grossProfit} priorValue={pyGross} kind="income" bold total color={grossProfit > 0 ? COLORS.success : COLORS.error} />
              <PLRow label="" />
              <PLRow label="OPERATING EXPENSES" bold color={COLORS.muted} />
              {(mapping?.opex || []).map((line, i) => {
                const val = lineVal('opex', line);
                const pyVal = lineVal('opex', line, true);
                if (Math.abs(val) < 0.005 && Math.abs(pyVal) < 0.005) return null;
                return <PLRow key={`op-${i}`} kind="expense" label={line.label} value={val} priorValue={pyVal} indent={1} />;
              })}
              <PLRow label="Total Operating Expenses" value={totalOpEx} priorValue={pyOpEx} kind="expense" bold total />
              <PLRow label="INCOME FROM OPERATIONS (EBIT)" value={ebit} priorValue={pyEBIT} kind="income" bold total color={ebit > 0 ? COLORS.dark : COLORS.error} />
              {((mapping?.other_income || []).some(l => Math.abs(lineVal('other_income', l)) > 0.005 || Math.abs(lineVal('other_income', l, true)) > 0.005) ||
                (mapping?.other_expense || []).some(l => Math.abs(lineVal('other_expense', l)) > 0.005 || Math.abs(lineVal('other_expense', l, true)) > 0.005) ||
                (mapping?.other || []).some(l => Math.abs(sum(l.accounts || [])) > 0.005)) && (
                <>
                  <PLRow label="" />
                  <PLRow label="OTHER INCOME / (EXPENSE)" bold color={COLORS.muted} />
                  {(mapping?.other_income || []).map((line, i) => {
                    const val = lineVal('other_income', line);
                    const pyVal = lineVal('other_income', line, true);
                    if (Math.abs(val) < 0.005 && Math.abs(pyVal) < 0.005) return null;
                    return <PLRow key={`othi-${i}`} kind="income" label={line.label} value={val} priorValue={pyVal} indent={1} />;
                  })}
                  {(mapping?.other_expense || []).map((line, i) => {
                    const val = lineVal('other_expense', line);
                    const pyVal = lineVal('other_expense', line, true);
                    if (Math.abs(val) < 0.005 && Math.abs(pyVal) < 0.005) return null;
                    return <PLRow key={`othe-${i}`} kind="expense" label={line.label} value={-val} priorValue={-pyVal} indent={1} />;
                  })}
                  {(mapping?.other || []).map((line, i) => {
                    const val = sum(line.accounts || []);
                    if (Math.abs(val) < 0.005) return null;
                    return <PLRow key={`othl-${i}`} label={line.label} value={val} indent={1} />;
                  })}
                </>
              )}
              <PLRow label="NET INCOME" value={netIncome} priorValue={pyNetIncome} kind="income" bold total color={netIncome > 0 ? COLORS.success : COLORS.error} />
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {/* Balance Sheet */}
      {activeReport === 'bs' && (
        <Card className="statement-card">
          <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px', color: COLORS.dark }}>Balance Sheet</h3>
          <div className="statement-sub" style={{ fontSize: 12, color: COLORS.muted, marginBottom: 24 }}>{balanceSheetDate}</div>
          <div className="bs-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>
            <div className="statement-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Assets</th>
                  <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>{ytdLabel}</th>
                  {hasPriorYearData && (
                    <th className="col-aux-hide-mobile" style={{ textAlign: 'right', padding: '8px 0 8px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>{priorYtdLabel}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Walk the canonical structured items (group + leaf) so the
                  // BS renders Section → Group → Leaf with subtotals. Cash &
                  // Equivalents is auto-promoted as a group inside Current
                  // Assets by lib/reports.js — no special-case here.
                  const caItems    = bsCanonical.sections.assetsCur.items;
                  const fixedItems = bsCanonical.sections.assetsFix.items;
                  const otherItems = bsCanonical.sections.assetsOther.items;
                  const caTotal    = bsCanonical.sections.assetsCur.totalCombined;
                  const caTotalPY  = bsCanonical.sections.assetsCur.totalCombined_prior;
                  return (
                    <>
                      {caItems.length > 0 && (
                        <>
                          <BSRow label="Current Assets" bold indent={0} />
                          <StructuredItems items={caItems} RowComponent={BSRow}
                                           showPrior={hasPriorYearData} baseIndent={1} />
                          <BSRow label="Total Current Assets"
                                 value={caTotal}
                                 priorValue={hasPriorYearData ? caTotalPY : undefined}
                                 bold total indent={0} />
                          <BSRow label="" />
                        </>
                      )}
                      {fixedItems.length > 0 && (
                        <>
                          <BSRow label="Property & Equipment" bold indent={0} />
                          <StructuredItems items={fixedItems} RowComponent={BSRow}
                                           showPrior={hasPriorYearData} baseIndent={1} />
                          <BSRow label="Total Property & Equipment"
                                 value={bsCanonical.sections.assetsFix.total}
                                 priorValue={hasPriorYearData ? bsCanonical.sections.assetsFix.total_prior : undefined}
                                 bold total indent={0} />
                          <BSRow label="" />
                        </>
                      )}
                      {otherItems.length > 0 && (
                        <>
                          <BSRow label="Other Assets" bold indent={0} />
                          <StructuredItems items={otherItems} RowComponent={BSRow}
                                           showPrior={hasPriorYearData} baseIndent={1} />
                          <BSRow label="Total Other Assets"
                                 value={bsCanonical.sections.assetsOther.total}
                                 priorValue={hasPriorYearData ? bsCanonical.sections.assetsOther.total_prior : undefined}
                                 bold total indent={0} />
                          <BSRow label="" />
                        </>
                      )}
                      <BSRow label="TOTAL ASSETS"
                             value={totalAssets}
                             priorValue={hasPriorYearData ? bsCanonical.prior.totalAssets : undefined}
                             bold total indent={0} />
                    </>
                  );
                })()}
              </tbody>
            </table>
            </div>
            <div className="statement-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Liabilities & Equity</th>
                  <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>{ytdLabel}</th>
                  {hasPriorYearData && (
                    <th className="col-aux-hide-mobile" style={{ textAlign: 'right', padding: '8px 0 8px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>{priorYtdLabel}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const clItems    = bsCanonical.sections.liabCur.items;
                  const shortItems = bsCanonical.sections.debtShort.items;
                  const ltItems    = bsCanonical.sections.liabLong.items;
                  const equityItems = bsCanonical.sections.equity.items;
                  // PY totals — drive from bsCanonical.prior so PY Assets and
                  // PY L+E foot exactly. The per-section subtotals below
                  // (e.g. Total Current Liabilities) use the per-section
                  // largest-remainder rounded total_prior and may differ from
                  // their sum by sub-cent rounding — that's expected and is
                  // why the grand-total rows must come from bsCanonical.prior
                  // not from re-summing the individual sections.
                  const pyTotalLiab   = bsCanonical.prior.totalLiab;
                  const pyTotalEquity = bsCanonical.prior.totalEquity;
                  const pyTotalLE     = bsCanonical.prior.totalLE;
                  return (
                    <>
                      {clItems.length > 0 && (
                        <>
                          <BSRow label="Current Liabilities" bold indent={0} />
                          <StructuredItems items={clItems} RowComponent={BSRow}
                                           showPrior={hasPriorYearData} baseIndent={1} />
                          <BSRow label="Total Current Liabilities" value={totalCL}
                                 priorValue={hasPriorYearData ? sumSection('liabilities_current', true) : undefined}
                                 bold total indent={0} />
                          <BSRow label="" />
                        </>
                      )}
                      {shortItems.length > 0 && (
                        <>
                          <BSRow label="Short Term Debt" bold indent={0} />
                          <StructuredItems items={shortItems} RowComponent={BSRow}
                                           showPrior={hasPriorYearData} baseIndent={1} />
                          <BSRow label="Total Short Term Debt" value={shortTermDebt}
                                 priorValue={hasPriorYearData ? sumSection('debt_short', true) : undefined}
                                 bold total indent={0} />
                          <BSRow label="" />
                        </>
                      )}
                      {ltItems.length > 0 && (
                        <>
                          <BSRow label="Long-Term Liabilities" bold indent={0} />
                          <StructuredItems items={ltItems} RowComponent={BSRow}
                                           showPrior={hasPriorYearData} baseIndent={1} />
                          <BSRow label="Total Long-Term Liabilities" value={totalLTD}
                                 priorValue={hasPriorYearData ? sumSection('liabilities_long', true) : undefined}
                                 bold total indent={0} />
                          <BSRow label="" />
                        </>
                      )}
                      <BSRow label="TOTAL LIABILITIES" value={totalLiab}
                             priorValue={hasPriorYearData ? pyTotalLiab : undefined} bold indent={0} />
                      <BSRow label="" />
                      <BSRow label={equityLabels.equity} bold indent={0} />
                      <StructuredItems items={equityItems} RowComponent={BSRow}
                                       showPrior={hasPriorYearData} baseIndent={1} />
                      <BSRow label="Current Year Earnings" value={netIncome}
                             priorValue={hasPriorYearData ? pyNetIncome : undefined} indent={1} />
                      <BSRow label="Total Equity" value={totalEquity}
                             priorValue={hasPriorYearData ? pyTotalEquity : undefined} bold total indent={0} />
                      <BSRow label="" />
                      <BSRow label="TOTAL LIABILITIES & EQUITY" value={totalLE}
                             priorValue={hasPriorYearData ? pyTotalLE : undefined}
                             bold total indent={0} />
                    </>
                  );
                })()}
              </tbody>
            </table>
            </div>
          </div>
          <div style={{
            marginTop: 20,
            padding: '12px 16px',
            background: Math.abs(totalAssets - totalLE) < 1 ? '#F0FDF4' : '#FEF2F2',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: Math.abs(totalAssets - totalLE) < 1 ? COLORS.success : COLORS.error,
          }}>
            {Math.abs(totalAssets - totalLE) < 1 ? '✓ Balance sheet is in balance' : `⚠ Out of balance by ${formatCurrency(Math.abs(totalAssets - totalLE))}`}
          </div>
        </Card>
      )}

      {/* Cash Flow */}
      {activeReport === 'cf' && (() => {
        // Use canonical buildCashFlow — single source of truth shared with the
        // validation banner so the gate ties to what's actually rendered.
        // V15: pass materiality threshold (engagement setting) + total
        // assets so the auto-formula default works.
        const totalAssetsForCF = (
          sectionTotal(accounts, mapping, 'cash') +
          sectionTotal(accounts, mapping, 'assets_current') +
          sectionTotal(accounts, mapping, 'assets_fixed') +
          sectionTotal(accounts, mapping, 'assets_other')
        );
        const cf = buildCashFlow(accounts, mapping, netIncome, pyNetIncome, {
          materialityThreshold: client?.cf_materiality_threshold,
          totalAssets: totalAssetsForCF,
          niAtPeriodStart: qboMeta?.ni_at_period_start,
          plPeriod: qboMeta?.pl_period,
          niAtPeriodEnd: netIncome,
          cashAtPeriodStart: qboMeta?.cash_at_period_start,
        });
        const cfDraftLifted = !validationResult.cashFlowDraft;
        const cfRows = [
          { label: 'OPERATING ACTIVITIES', bold: true },
          ...cf.sections.operating.rows.map(r => ({ ...r, indent: 1 })),
          { label: 'Net Cash from Operating Activities', value: cf.sections.operating.subtotal, bold: true, total: true },
          { label: '' },
          { label: 'INVESTING ACTIVITIES', bold: true },
          ...cf.sections.investing.rows.map(r => ({ ...r, indent: 1 })),
          { label: 'Net Cash from Investing Activities', value: cf.sections.investing.subtotal, bold: true, total: true },
          { label: '' },
          { label: 'FINANCING ACTIVITIES', bold: true },
          ...cf.sections.financing.rows.map(r => ({ ...r, indent: 1 })),
          { label: 'Net Cash from Financing Activities', value: cf.sections.financing.subtotal, bold: true, total: true },
          { label: '' },
          { label: 'NET CHANGE IN CASH', value: cf.netChange, bold: true },
          { label: 'Beginning Cash Balance', value: cf.beginCash, indent: 1 },
          { label: 'ENDING CASH BALANCE', value: cf.endCash, bold: true, total: true },
        ];
        if (cf.supplemental.interestPaid || cf.supplemental.taxesPaid) {
          cfRows.push({ label: '' });
          cfRows.push({ label: 'SUPPLEMENTAL DISCLOSURES OF CASH FLOW INFORMATION', bold: true });
          if (cf.supplemental.interestPaid) {
            cfRows.push({ label: 'Cash paid for interest', value: cf.supplemental.interestPaid, indent: 1 });
          }
          if (cf.supplemental.taxesPaid) {
            cfRows.push({ label: 'Cash paid for income taxes', value: cf.supplemental.taxesPaid, indent: 1 });
          }
        }

        return (
        <Card className="statement-card">
          <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px', color: COLORS.dark }}>Statement of Cash Flows</h3>
          <div className="statement-sub" style={{ fontSize: 12, color: COLORS.muted, marginBottom: 16 }}>{period} · Indirect Method</div>
          {!cfDraftLifted && (
            <div
              data-testid="cf-draft-banner"
              style={{
                marginBottom: 16, padding: '10px 14px', background: '#FFF7ED',
                border: '1px solid #FDBA74', borderRadius: 8, fontSize: 12,
                color: '#7C2D12', display: 'flex', alignItems: 'flex-start', gap: 8,
              }}
            >
              <span style={{ fontWeight: 700, color: '#9A3412' }}>DRAFT — NOT FOR DELIVERY.</span>
              <span>
                Cash Flow ties (NI ↔ IS, Ending Cash ↔ BS) have not yet passed
                for this period. The Snapshot publish endpoint will refuse to
                include this statement until both ties are green.
              </span>
            </div>
          )}
          {cfDraftLifted && (
            <div
              data-testid="cf-validated-banner"
              style={{
                marginBottom: 16, padding: '10px 14px', background: '#E8F5EE',
                border: '1px solid #1F9D57', borderRadius: 8, fontSize: 12,
                color: '#0F5A2F', display: 'flex', alignItems: 'flex-start', gap: 8,
              }}
            >
              <span style={{ fontWeight: 700, color: '#0F5A2F' }}>✓ Validated.</span>
              <span>
                Cash Flow Net Income ties to Income Statement and Ending Cash
                ties to Balance Sheet Cash & Equivalents.
              </span>
            </div>
          )}
          {!cf.hasPrior && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#FEF3C7', borderRadius: 8, fontSize: 12, color: '#92400E' }}>
              Working capital changes require prior-period data. Connect/sync QuickBooks to populate the prior year for a complete cash flow statement.
            </div>
          )}
          {/* V21 — Bookkeeping anomaly advisory. The SCF D&A add-back is
              sourced from the BS Accumulated Depreciation delta (the
              accounting ground truth), not from the IS Depreciation
              Expense line. When the two diverge by more than max($1,000,
              1% of period revenue), it's a real CPA signal — Section 179
              carryovers, asset disposals booked through P&L, expense
              reclassifications hitting depreciation, etc. */}
          {(() => {
            const periodRevenue = Math.abs(
              sectionTotal(accounts, mapping, 'income') +
              sectionTotal(accounts, mapping, 'other_income')
            );
            const tol = Math.max(1000, periodRevenue * 0.01);
            const variance = Math.abs(cf.dAndA_variance || 0);
            if (variance < tol) return null;
            const fmt = (v) => formatCurrency(v);
            return (
              <div
                data-testid="cf-bookkeeping-anomaly"
                style={{
                  marginBottom: 16, padding: '10px 14px', background: '#FEF3C7',
                  border: '1px solid #D97706', borderRadius: 8, fontSize: 12,
                  color: '#7C2D12', display: 'flex', alignItems: 'flex-start', gap: 10,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14, color: '#9A3412' }}>⚠</span>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    Bookkeeping anomaly — D&amp;A sources disagree
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    D&amp;A on the Statement of Cash Flows is derived from the
                    change in Accumulated Depreciation on the Balance Sheet
                    (<strong>{fmt(cf.dAndA_BS)}</strong>).
                  </div>
                  <div style={{ marginBottom: 3 }}>
                    The Income Statement reports Depreciation &amp; Amortization
                    Expense of <strong>{fmt(cf.dAndA_IS)}</strong>.
                  </div>
                  <div>
                    The difference of <strong>{fmt(cf.dAndA_variance)}</strong>{' '}
                    typically reflects a Section 179 carryover, an expense
                    reclassification, or an asset disposal booked through the
                    P&amp;L without touching Accumulated Depreciation. Investigate
                    in QuickBooks if unexpected.
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="statement-table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Description</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {cfRows.map((row, i) => (
                <tr key={i} style={{ borderBottom: row.total ? `2px solid ${COLORS.dark}` : 'none', fontStyle: row.memo ? 'italic' : 'normal', opacity: row.memo ? 0.65 : 1 }}
                    data-testid={row.label === 'ENDING CASH BALANCE' ? 'cf-ending-cash' : (row.label === 'Net Income' ? 'cf-net-income' : undefined)}>
                  <td style={{ padding: '6px 0', paddingLeft: (row.indent || 0) * 20, fontSize: row.bold ? 13 : 12, fontWeight: row.bold ? 700 : 400, color: COLORS.dark }}>{row.label}</td>
                  <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: row.bold ? 13 : 12, fontWeight: row.bold ? 700 : 400, color: row.value < 0 ? COLORS.error : COLORS.dark }}>
                    {row.value !== undefined ? formatCurrency(row.value) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
        );
      })()}

      {/* Trailing 12 Months P&L — canonical per-account matrix.
          Each canonical IS row maps to 12 monthly columns + total. Net Margin
          row uses em-dash for divide-by-zero. */}
      {activeReport === 'ttm' && (() => {
        const ttmMonths = ttmData?.months || [];
        const ttmStart = ttmData?.period_start;
        const ttmEnd = ttmData?.period_end;
        const headerSub = ttmMonths.length > 0
          ? `${ttmStart} — ${ttmEnd} · sourced from QuickBooks Online`
          : 'Rolling 12-month from QuickBooks';
        const monthLabels = ttmMonths.map(m => `${m.month} ${String(m.year).slice(-2)}`);
        const t12 = ttmData?.detail_lines_monthly
          ? buildT12(accounts, mapping, ttmData, monthLabels)
          : buildT12(ttmData?.accounts_monthly || [], mapping, monthLabels);
        const fmtCell = (v) => v === undefined || v === null
          ? '—'
          : (Math.abs(v) < 0.005 ? '—' : formatCurrency(v));
        const fmtMargin = (v) => (v === null || v === undefined)
          ? '—'
          : `${v.toFixed(1)}%`;
        return (
        <Card className="statement-card">
          <div className="statement-meta-row" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 500, margin: '0 0 4px', color: COLORS.dark }}>Trailing 12 Months Statement of Income</h3>
              <div className="statement-sub" style={{ fontSize: 12, color: COLORS.muted }}>{headerSub}</div>
            </div>
            <div data-testid="t12-validated-banner" style={{
              padding: '6px 12px', background: '#E8F5EE', border: '1px solid #1F9D57',
              borderRadius: 8, fontSize: 11, color: '#0F5A2F', fontWeight: 600,
            }}>
              ✓ Per-account · canonical mapping
            </div>
          </div>
          {ttmLoading && (
            <div style={{ padding: 24, textAlign: 'center', color: COLORS.muted, fontSize: 13 }}>
              <Loader2 size={18} className="animate-spin" style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Pulling 12 months of P&L from QuickBooks…
            </div>
          )}
          {!ttmLoading && ttmMonths.length === 0 && (
            <div style={{ padding: '12px 16px', background: '#FEF3C7', borderRadius: 8, fontSize: 13, color: '#92400E' }}>
              Trailing-12-month data isn't loaded yet. Connect/sync QuickBooks Online then refresh.
            </div>
          )}
          {!ttmLoading && ttmMonths.length > 0 && (
          <div className="ttm-container" style={{ overflowX: 'auto' }}>
          <table data-testid="t12-table" style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                <th style={{ padding: '6px 4px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: COLORS.muted, position: 'sticky', left: 0, background: COLORS.card, width: 160 }}>Account</th>
                {t12.months.map((m, i) => (
                  <th key={i} className="col-month-hide-mobile" style={{ padding: '6px 3px', textAlign: 'right', fontSize: 9.5, fontWeight: 700, color: COLORS.muted }}>{m}</th>
                ))}
                <th style={{ padding: '6px 4px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.dark, background: COLORS.primaryLight, width: 80 }}>T12 Total</th>
              </tr>
            </thead>
            <tbody>
              {t12.sections.map((sec, si) => (
                <React.Fragment key={`sec-${si}`}>
                  {!sec.isSubtotal && !sec.isMargin && (
                    <tr>
                      <td colSpan={t12.months.length + 2} style={{ padding: '6px 4px 3px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted, position: 'sticky', left: 0, background: COLORS.card }}>{sec.label}</td>
                    </tr>
                  )}
                  {(sec.rows || []).map((row, ri) => {
                    const indent = row.kind === 'leaf' ? 1 : 0;
                    const indentExtra = (row.kind === 'leaf' && (sec.rows[ri - 1]?.kind === 'group_header')) ? 1 : 0;
                    const isHeader   = row.kind === 'group_header';
                    const isSubtotal = row.kind === 'group_subtotal';
                    const fw = (isHeader || isSubtotal) ? 600 : 400;
                    return (
                      <tr key={`r-${si}-${ri}`} style={{ borderBottom: isSubtotal ? `1px solid ${COLORS.border}` : 'none' }}>
                        <td style={{ padding: '3px 4px', paddingLeft: 4 + (indent + indentExtra) * 10, fontSize: 11, fontWeight: fw, color: COLORS.dark, position: 'sticky', left: 0, background: COLORS.card, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.label}>{row.label}</td>
                        {(row.months || Array(t12.months.length).fill(0)).map((v, mi) => (
                          <td key={mi} className="col-month-hide-mobile" style={{ padding: '3px 3px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: fw, color: v < 0 ? COLORS.error : COLORS.dark }}>{isHeader ? '' : fmtCell(v)}</td>
                        ))}
                        <td style={{ padding: '3px 4px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: fw, background: COLORS.primaryLight, color: row.total < 0 ? COLORS.error : COLORS.dark }}>{isHeader ? '' : fmtCell(row.total)}</td>
                      </tr>
                    );
                  })}
                  {sec.isSubtotal && (
                    <tr style={{ borderTop: `1px solid ${COLORS.dark}`, borderBottom: `2px solid ${COLORS.dark}`, background: '#F9FAFB' }} data-testid={`t12-subtotal-${sec.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <td style={{ padding: '6px 4px', fontSize: 11, fontWeight: 700, color: COLORS.dark, position: 'sticky', left: 0, background: '#F9FAFB' }}>{sec.label}</td>
                      {(sec.monthsTotal || []).map((v, mi) => (
                        <td key={mi} className="col-month-hide-mobile" style={{ padding: '6px 3px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: 700, color: v < 0 ? COLORS.error : COLORS.dark }}>{fmtCell(v)}</td>
                      ))}
                      <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: 700, background: COLORS.primaryLight, color: sec.total < 0 ? COLORS.error : COLORS.dark }}>{fmtCell(sec.total)}</td>
                    </tr>
                  )}
                  {sec.isMargin && (
                    <tr style={{ background: '#F9FAFB' }} data-testid="t12-net-margin-row">
                      <td style={{ padding: '6px 4px', fontSize: 11, fontWeight: 600, color: COLORS.muted, position: 'sticky', left: 0, background: '#F9FAFB' }}>{sec.label}</td>
                      {(sec.monthsTotal || []).map((v, mi) => (
                        <td key={mi} className="col-month-hide-mobile" style={{ padding: '6px 3px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: 600, color: COLORS.muted }}>{fmtMargin(v)}</td>
                      ))}
                      <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 10, fontWeight: 700, background: COLORS.primaryLight, color: COLORS.muted }}>{fmtMargin(sec.total)}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          )}
        </Card>
        );
      })()}

      {/* V19.4 — Standardized KPIs tab. Computes 12 industry-agnostic
          KPIs from canonical mapping + IS/BS/T12 outputs. Cards group
          into Liquidity / Profitability / Efficiency / Growth. */}
      {activeReport === 'kpis' && (() => {
        const periodLabel = qboMeta?.period_label || defaultPeriodLabels?.()?.current || '';
        const kpiCards = computeKpis({
          is: isCanonical,
          bs: bsCanonical,
          t12: ttmData,
          periodLabel,
        });
        const statusColor = {
          good: '#10B981',
          warn: '#F59E0B',
          bad: '#EF4444',
          neutral: COLORS.muted,
        };
        return (
          <div data-testid="kpis-tab" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <div style={{ fontSize: 12, color: COLORS.muted }}>
              Standardized financial KPIs derived from the canonical mapping.
              Targets are industry-agnostic defaults; per-client overrides
              coming in a later release. Cards labeled "n/a" indicate the
              underlying canonical line is missing or zero for this engagement.
            </div>
            {KPI_GROUPS.map(grp => {
              const cards = kpiCards.filter(k => k.group === grp.id);
              if (cards.length === 0) return null;
              return (
                <div key={grp.id} data-testid={`kpi-group-${grp.id}`}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: COLORS.muted,
                    marginBottom: 12,
                  }}>
                    {grp.label}
                  </div>
                  <div className="kpi-auto-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: 16,
                  }}>
                    {cards.map(k => (
                      <Card
                        key={k.id}
                        data-testid={`kpi-card-${k.id}`}
                        style={{
                          padding: 18,
                          borderTop: `4px solid ${statusColor[k.status] || COLORS.muted}`,
                        }}
                      >
                        <div style={{
                          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                          textTransform: 'uppercase', color: COLORS.muted,
                          marginBottom: 8,
                        }}>
                          {k.label}
                        </div>
                        <div className="font-heading" style={{
                          fontSize: 24, fontWeight: 600, color: COLORS.dark,
                          marginBottom: 6,
                        }}>
                          {k.valueFormatted}
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.muted, lineHeight: 1.4 }}>
                          {k.n_a || k.sub}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* V19.2 — Debt Schedule body gated on actual LT/short debt balances.
          When no debt exists, the tab is hidden (above) so this body
          shouldn't render either; this guard is defense-in-depth for
          stuck activeReport state during the setTimeout reset cycle. */}
      {activeReport === 'debt'
        && (Math.abs(bsCanonical.sections.liabLong?.total || 0)
            + Math.abs(bsCanonical.sections.debtShort?.total || 0)) >= 0.5
        && (
        <DebtScheduleModule
          client={client}
          ltLoans={bsCanonical.sections.liabLong?.items || []}
          shortLoans={bsCanonical.sections.debtShort?.items || []}
        />
      )}

      {/* V19.2 — Fixed Assets body gated on actual fixed-asset balance. */}
      {activeReport === 'assets'
        && Math.abs(bsCanonical.sections.assetsFix?.total || 0) >= 0.5
        && (
        <FixedAssetModule
          client={client}
          fixedAssetLines={bsCanonical.sections.assetsFix?.items || []}
        />
      )}

      {/* V19.2 — Consolidation body gated on consolidation-group flag.
          For standalone clients the tab is hidden (above). Mock data
          inside ConsolidationModule will be rebuilt under V19.4. */}
      {activeReport === 'consolidation' && client?.is_consolidation_group === true && (
        <ConsolidationModule clients={[client]} accounts={accounts} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
//   CONSOLIDATION MODULE
// ═══════════════════════════════════════════════════
function ConsolidationModule({ clients, accounts }) {
  // Sample consolidated data - in real implementation, would fetch from API
  const entities = [
    { name: 'GT Farm Services', revenue: 1891400, cogs: 905400, opex: 609900, netIncome: 301150, assets: 1325150, liab: 709500 },
    { name: 'GT Equipment Rental', revenue: 524000, cogs: 262000, opex: 157200, netIncome: 89280, assets: 892000, liab: 445000 },
    { name: 'GT Land Holdings', revenue: 180000, cogs: 0, opex: 45000, netIncome: 121500, assets: 2150000, liab: 1290000 },
  ];
  
  const totals = entities.reduce((acc, e) => ({
    revenue: acc.revenue + e.revenue,
    cogs: acc.cogs + e.cogs,
    opex: acc.opex + e.opex,
    netIncome: acc.netIncome + e.netIncome,
    assets: acc.assets + e.assets,
    liab: acc.liab + e.liab,
  }), { revenue: 0, cogs: 0, opex: 0, netIncome: 0, assets: 0, liab: 0 });

  return (
    <div>
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h3 className="font-heading" style={{ fontSize: 18, fontWeight: 500, color: COLORS.dark, margin: 0 }}>Entity Consolidation</h3>
            <p style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>View and compare financials across related entities</p>
          </div>
          <button style={{
            padding: '8px 16px',
            borderRadius: 6,
            border: `1px solid ${COLORS.border}`,
            background: COLORS.card,
            color: COLORS.muted,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}>
            + Add Entity
          </button>
        </div>

        {/* Consolidated Income Statement */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 12 }}>Consolidated Statement of Income</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>DESCRIPTION</th>
                {entities.map(e => (
                  <th key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>{e.name.toUpperCase()}</th>
                ))}
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.primary, background: COLORS.primaryLight }}>CONSOLIDATED</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '8px 0', fontSize: 12, fontWeight: 600 }}>Total Revenue</td>
                {entities.map(e => <td key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12 }}>{formatCurrency(e.revenue)}</td>)}
                <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, fontWeight: 700, background: COLORS.primaryLight }}>{formatCurrency(totals.revenue)}</td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '8px 0', fontSize: 12 }}>Cost of Revenue</td>
                {entities.map(e => <td key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.error }}>{formatCurrency(e.cogs)}</td>)}
                <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.error, fontWeight: 700, background: COLORS.primaryLight }}>{formatCurrency(totals.cogs)}</td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '8px 0', fontSize: 12, fontWeight: 600 }}>Gross Profit</td>
                {entities.map(e => <td key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.success }}>{formatCurrency(e.revenue - e.cogs)}</td>)}
                <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.success, fontWeight: 700, background: COLORS.primaryLight }}>{formatCurrency(totals.revenue - totals.cogs)}</td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '8px 0', fontSize: 12 }}>Operating Expenses</td>
                {entities.map(e => <td key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.error }}>{formatCurrency(e.opex)}</td>)}
                <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.error, fontWeight: 700, background: COLORS.primaryLight }}>{formatCurrency(totals.opex)}</td>
              </tr>
              <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                <td style={{ padding: '10px 0', fontSize: 13, fontWeight: 700 }}>Net Income</td>
                {entities.map(e => <td key={e.name} style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, fontWeight: 700, color: e.netIncome > 0 ? COLORS.success : COLORS.error }}>{formatCurrency(e.netIncome)}</td>)}
                <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, fontWeight: 700, color: COLORS.success, background: COLORS.primaryLight }}>{formatCurrency(totals.netIncome)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Consolidated Balance Sheet Summary */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 12 }}>Consolidated Balance Sheet Summary</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>DESCRIPTION</th>
                {entities.map(e => (
                  <th key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>{e.name.toUpperCase()}</th>
                ))}
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.primary, background: COLORS.primaryLight }}>CONSOLIDATED</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '8px 0', fontSize: 12, fontWeight: 600 }}>Total Assets</td>
                {entities.map(e => <td key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12 }}>{formatCurrency(e.assets)}</td>)}
                <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, fontWeight: 700, background: COLORS.primaryLight }}>{formatCurrency(totals.assets)}</td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '8px 0', fontSize: 12 }}>Total Liabilities</td>
                {entities.map(e => <td key={e.name} style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.error }}>{formatCurrency(e.liab)}</td>)}
                <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.error, fontWeight: 700, background: COLORS.primaryLight }}>{formatCurrency(totals.liab)}</td>
              </tr>
              <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                <td style={{ padding: '10px 0', fontSize: 13, fontWeight: 700 }}>Total Equity</td>
                {entities.map(e => <td key={e.name} style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, fontWeight: 700, color: COLORS.success }}>{formatCurrency(e.assets - e.liab)}</td>)}
                <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, fontWeight: 700, color: COLORS.success, background: COLORS.primaryLight }}>{formatCurrency(totals.assets - totals.liab)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <div style={{ padding: 16, background: COLORS.subtle, borderRadius: 8, fontSize: 12, color: COLORS.muted }}>
        <strong>Note:</strong> Consolidated totals represent the sum of all entities. Intercompany eliminations may be required for GAAP-compliant consolidation.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
//   DEBT SCHEDULE MODULE
// ═══════════════════════════════════════════════════
// V19.2 — Debt Schedule pulls from the canonical mapping's debt
// sections instead of hardcoded mock loans. Each canonical line in
// liab_long_term + liab_debt_short becomes one row in the schedule.
// Amortization, rate, lender details are NOT in QBO — staff add
// those manually via the "Add Loan" form, keyed by the canonical
// account ID. (Future: persist these to /api/clients/:id/loans.)
function DebtScheduleModule({ client, ltLoans = [], shortLoans = [] }) {
  // Derive initial loan rows from canonical mapping. Each canonical
  // line carries a label and current balance; rate/payment/maturity
  // require manual entry by staff (QBO doesn't store them). Default
  // payment/rate to 0 so the amortization table is honest rather
  // than fabricated.
  const initialLoans = [...shortLoans, ...ltLoans]
    .filter(line => Math.abs(line.amount || line.balance || 0) >= 0.5)
    .map((line, idx) => ({
      id: `mapping-${idx}`,
      lender: line.label || `Loan ${idx + 1}`,
      payment: 0,
      rate: 0,
      numPayments: 0,
      maturity: '',
      balance: Math.abs(line.amount || line.balance || 0),
      _fromMapping: true,
    }));
  const [loans, setLoans] = useState(initialLoans);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLoan, setNewLoan] = useState({ lender: '', payment: '', rate: '', numPayments: '', maturity: '', balance: '' });

  const calculateAmortization = (loan) => {
    const monthlyRate = loan.rate / 100 / 12;
    let balance = loan.balance;
    const schedule = [];
    
    for (let i = 1; i <= Math.min(loan.numPayments, 60); i++) {
      const interest = balance * monthlyRate;
      const principal = loan.payment - interest;
      balance = Math.max(0, balance - principal);
      
      const date = new Date();
      date.setMonth(date.getMonth() + i);
      
      schedule.push({
        payment: i,
        date: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        principal: principal,
        interest: interest,
        total: loan.payment,
        balance: balance,
      });
    }
    return schedule;
  };

  const handleAddLoan = () => {
    if (newLoan.lender && newLoan.payment && newLoan.rate) {
      setLoans([...loans, {
        id: loans.length + 1,
        lender: newLoan.lender,
        payment: parseFloat(newLoan.payment),
        rate: parseFloat(newLoan.rate),
        numPayments: parseInt(newLoan.numPayments) || 60,
        maturity: newLoan.maturity,
        balance: parseFloat(newLoan.balance) || parseFloat(newLoan.payment) * parseInt(newLoan.numPayments),
      }]);
      setNewLoan({ lender: '', payment: '', rate: '', numPayments: '', maturity: '', balance: '' });
      setShowAddForm(false);
    }
  };

  const totalDebt = loans.reduce((s, l) => s + l.balance, 0);
  const totalMonthlyPayment = loans.reduce((s, l) => s + l.payment, 0);

  return (
    <div>
      {/* Summary Cards */}
      <div className="dash-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 8 }}>Total Debt</div>
          <div className="font-heading" style={{ fontSize: 24, fontWeight: 500, color: COLORS.dark }}>{formatCurrency(totalDebt)}</div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 8 }}>Monthly Payments</div>
          <div className="font-heading" style={{ fontSize: 24, fontWeight: 500, color: COLORS.dark }}>{formatCurrency(totalMonthlyPayment)}</div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 8 }}>Number of Loans</div>
          <div className="font-heading" style={{ fontSize: 24, fontWeight: 500, color: COLORS.dark }}>{loans.length}</div>
        </Card>
      </div>

      {/* Loans List */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: COLORS.dark, margin: 0 }}>Active Loans</h3>
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              background: COLORS.primary,
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Plus size={14} /> Add Loan
          </button>
        </div>

        {showAddForm && (
          <div style={{ padding: 16, background: COLORS.off, borderRadius: 8, marginBottom: 16 }}>
            <div className="loan-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
              <input placeholder="Lender Name" value={newLoan.lender} onChange={e => setNewLoan({...newLoan, lender: e.target.value})} style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
              <input placeholder="Payment Amount" type="number" value={newLoan.payment} onChange={e => setNewLoan({...newLoan, payment: e.target.value})} style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
              <input placeholder="Interest Rate %" type="number" step="0.01" value={newLoan.rate} onChange={e => setNewLoan({...newLoan, rate: e.target.value})} style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
              <input placeholder="Number of Payments" type="number" value={newLoan.numPayments} onChange={e => setNewLoan({...newLoan, numPayments: e.target.value})} style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
              <input placeholder="Maturity Date" type="date" value={newLoan.maturity} onChange={e => setNewLoan({...newLoan, maturity: e.target.value})} style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
              <input placeholder="Current Balance" type="number" value={newLoan.balance} onChange={e => setNewLoan({...newLoan, balance: e.target.value})} style={{ padding: '10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddLoan} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: COLORS.success, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Save Loan</button>
              <button onClick={() => setShowAddForm(false)} style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: '#fff', color: COLORS.dark, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        <div className="statement-table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
              <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 11, fontWeight: 700, color: COLORS.muted }}>Lender</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, color: COLORS.muted }}>Payment</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, color: COLORS.muted }}>Rate</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, color: COLORS.muted }}>Payments</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, color: COLORS.muted }}>Maturity</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 11, fontWeight: 700, color: COLORS.muted }}>Balance</th>
              <th style={{ padding: '8px 0', textAlign: 'center', fontSize: 11, fontWeight: 700, color: COLORS.muted }}>Schedule</th>
            </tr>
          </thead>
          <tbody>
            {loans.map(loan => (
              <tr key={loan.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '12px 0', fontSize: 13, fontWeight: 500 }}>{loan.lender}</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12 }}>{formatCurrency(loan.payment)}</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12 }}>{loan.rate}%</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12 }}>{loan.numPayments}</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontSize: 12, color: COLORS.muted }}>{new Date(loan.maturity).toLocaleDateString()}</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, fontWeight: 600 }}>{formatCurrency(loan.balance)}</td>
                <td style={{ padding: '12px 0', textAlign: 'center' }}>
                  <button
                    onClick={() => setSelectedLoan(selectedLoan?.id === loan.id ? null : loan)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 4,
                      border: `1px solid ${selectedLoan?.id === loan.id ? COLORS.primary : COLORS.border}`,
                      background: selectedLoan?.id === loan.id ? COLORS.primaryLight : 'transparent',
                      color: selectedLoan?.id === loan.id ? COLORS.primary : COLORS.muted,
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    {selectedLoan?.id === loan.id ? 'Hide' : 'View'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      {/* Amortization Schedule */}
      {selectedLoan && (
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: COLORS.dark, margin: '0 0 4px' }}>Amortization Schedule</h3>
          <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 20 }}>{selectedLoan.lender} — {formatCurrency(selectedLoan.balance)} at {selectedLoan.rate}%</div>
          
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: COLORS.card }}>
                <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                  <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>#</th>
                  <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Date</th>
                  <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Principal</th>
                  <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Interest</th>
                  <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Total</th>
                  <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {calculateAmortization(selectedLoan).map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '6px 0', fontSize: 11, color: COLORS.muted }}>{row.payment}</td>
                    <td style={{ padding: '6px 0', fontSize: 11 }}>{row.date}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 11, color: COLORS.success }}>{formatCurrency(row.principal)}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 11, color: COLORS.error }}>{formatCurrency(row.interest)}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 11 }}>{formatCurrency(row.total)}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 11, fontWeight: 500 }}>{formatCurrency(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
//   FIXED ASSET MODULE
// ═══════════════════════════════════════════════════
function FixedAssetModule({ client, fixedAssetLines = [] }) {
  // V19.2 — Initial asset rows derived from canonical mapping. Each
  // mapped fixed-asset line becomes a placeholder row. Purchase date,
  // depreciation life, and original cost are NOT in QBO's balance
  // sheet position — staff fills those in via the add form. The
  // placeholder rows expose the canonical balance as the current
  // book value so the totals tie to the BS face.
  const initialAssets = fixedAssetLines
    .filter(line => Math.abs(line.amount || line.balance || 0) >= 0.5)
    .map((line, idx) => ({
      id: `mapping-${idx}`,
      description: line.label || `Fixed Asset ${idx + 1}`,
      purchaseDate: '',
      purchasePrice: Math.abs(line.amount || line.balance || 0),
      depreciationLife: 5,
      _fromMapping: true,
    }));
  const [assets, setAssets] = useState(initialAssets);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAsset, setNewAsset] = useState({ description: '', purchaseDate: '', purchasePrice: '', depreciationLife: '5' });

  const calculateDepreciation = (asset) => {
    // V19.2 — Mapping-derived rows have no purchase date until staff
    // fills it in. Return zeros so totals stay honest and the rows
    // render with "—" placeholders rather than NaN.
    if (!asset.purchaseDate) {
      return { annualDepreciation: 0, accumulatedDepreciation: 0, bookValue: asset.purchasePrice, yearsRemaining: 0 };
    }
    const purchaseDate = new Date(asset.purchaseDate);
    const today = new Date();
    const yearsHeld = (today - purchaseDate) / (1000 * 60 * 60 * 24 * 365.25);
    const annualDepreciation = asset.purchasePrice / asset.depreciationLife;
    const accumulatedDepreciation = Math.min(annualDepreciation * yearsHeld, asset.purchasePrice);
    const bookValue = Math.max(0, asset.purchasePrice - accumulatedDepreciation);
    return { annualDepreciation, accumulatedDepreciation, bookValue, yearsRemaining: Math.max(0, asset.depreciationLife - yearsHeld) };
  };

  const handleAddAsset = () => {
    if (newAsset.description && newAsset.purchaseDate && newAsset.purchasePrice) {
      setAssets([...assets, {
        id: assets.length + 1,
        description: newAsset.description,
        purchaseDate: newAsset.purchaseDate,
        purchasePrice: parseFloat(newAsset.purchasePrice),
        depreciationLife: parseInt(newAsset.depreciationLife) || 5,
      }]);
      setNewAsset({ description: '', purchaseDate: '', purchasePrice: '', depreciationLife: '5' });
      setShowAddForm(false);
    }
  };

  const totalCost = assets.reduce((s, a) => s + a.purchasePrice, 0);
  const totalAccumDepr = assets.reduce((s, a) => s + calculateDepreciation(a).accumulatedDepreciation, 0);
  const totalBookValue = assets.reduce((s, a) => s + calculateDepreciation(a).bookValue, 0);
  const annualDeprExpense = assets.reduce((s, a) => s + calculateDepreciation(a).annualDepreciation, 0);

  return (
    <div>
      {/* Summary Cards */}
      <div className="dash-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 8 }}>Total Cost</div>
          <div className="font-heading" style={{ fontSize: 24, fontWeight: 500, color: COLORS.dark }}>{formatCurrency(totalCost)}</div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 8 }}>Accum. Depreciation</div>
          <div className="font-heading" style={{ fontSize: 24, fontWeight: 500, color: COLORS.error }}>{formatCurrency(totalAccumDepr)}</div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 8 }}>Book Value</div>
          <div className="font-heading" style={{ fontSize: 24, fontWeight: 500, color: COLORS.success }}>{formatCurrency(totalBookValue)}</div>
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 8 }}>Annual Expense</div>
          <div className="font-heading" style={{ fontSize: 24, fontWeight: 500, color: COLORS.dark }}>{formatCurrency(annualDeprExpense)}</div>
        </Card>
      </div>

      {/* Asset List */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="font-heading" style={{ fontSize: 18, fontWeight: 500, color: COLORS.dark, margin: 0 }}>Fixed Asset Schedule</h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              background: COLORS.primary,
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Plus size={14} /> Add Asset
          </button>
        </div>

        {showAddForm && (
          <div style={{ padding: 20, background: COLORS.subtle, borderRadius: 8, marginBottom: 20 }}>
            <div className="fa-form-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>Asset Description</label>
                <input
                  type="text"
                  value={newAsset.description}
                  onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
                  placeholder="e.g., 2024 Ford F-150"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>Date Purchased</label>
                <input
                  type="date"
                  value={newAsset.purchaseDate}
                  onChange={(e) => setNewAsset({ ...newAsset, purchaseDate: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>Purchase Price</label>
                <input
                  type="number"
                  value={newAsset.purchasePrice}
                  onChange={(e) => setNewAsset({ ...newAsset, purchasePrice: e.target.value })}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>Life (Years)</label>
                <select
                  value={newAsset.depreciationLife}
                  onChange={(e) => setNewAsset({ ...newAsset, depreciationLife: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, background: '#fff' }}
                >
                  <option value="3">3 Years</option>
                  <option value="5">5 Years</option>
                  <option value="7">7 Years</option>
                  <option value="15">15 Years</option>
                  <option value="27.5">27.5 Years</option>
                  <option value="39">39 Years</option>
                </select>
              </div>
              <button
                onClick={handleAddAsset}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: COLORS.success,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
            </div>
          </div>
        )}

        <div className="statement-table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
              <th style={{ padding: '8px 0', textAlign: 'left', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Asset Description</th>
              <th style={{ padding: '8px 0', textAlign: 'center', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Date Purchased</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Purchase Price</th>
              <th style={{ padding: '8px 0', textAlign: 'center', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Life</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Annual Depr.</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Accum. Depr.</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontSize: 10, fontWeight: 700, color: COLORS.muted }}>Book Value</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((asset, i) => {
              const depr = calculateDepreciation(asset);
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: '10px 0', fontSize: 13, fontWeight: 500 }}>{asset.description}</td>
                  <td style={{ padding: '10px 0', textAlign: 'center', fontSize: 12 }}>
                    {new Date(asset.purchaseDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12 }}>{formatCurrency(asset.purchasePrice)}</td>
                  <td style={{ padding: '10px 0', textAlign: 'center', fontSize: 12 }}>{asset.depreciationLife} yrs</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12 }}>{formatCurrency(depr.annualDepreciation)}</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, color: COLORS.error }}>{formatCurrency(depr.accumulatedDepreciation)}</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, fontWeight: 600, color: COLORS.success }}>{formatCurrency(depr.bookValue)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${COLORS.dark}`, fontWeight: 700 }}>
              <td colSpan={2} style={{ padding: '12px 0', fontSize: 13 }}>Totals</td>
              <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13 }}>{formatCurrency(totalCost)}</td>
              <td></td>
              <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13 }}>{formatCurrency(annualDeprExpense)}</td>
              <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, color: COLORS.error }}>{formatCurrency(totalAccumDepr)}</td>
              <td style={{ padding: '12px 0', textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 13, color: COLORS.success }}>{formatCurrency(totalBookValue)}</td>
            </tr>
          </tfoot>
        </table>
        </div>
      </Card>

      <div style={{ padding: 16, background: COLORS.subtle, borderRadius: 8, fontSize: 12, color: COLORS.muted }}>
        <strong>Note:</strong> Depreciation is calculated using the straight-line method. Annual depreciation = Purchase Price / Useful Life.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
//   STAFF DASHBOARD
// ═══════════════════════════════════════════════════
export default function StaffDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [step, setStep] = useState(0);
  const [view, setView] = useState('companies'); // 'companies' | 'dashboard'
  const [accounts, setAccounts] = useState(DEFAULT_ACCOUNTS);
  // QBO sync metadata — `reconciliation` (canonical vs QBO BS totals) and
  // `completeness` (any account dropped from TB ∪ BS) — used to gate publish
  // via the ValidationBanner. Reset to null whenever the client changes so a
  // stale recon doesn't shadow a fresh sync.
  const [qboMeta, setQboMeta] = useState(null);
  const [mapping, setMapping] = useState(DEFAULT_TEMPLATES['Service Business']);
  const [trendData, setTrendData] = useState(DEFAULT_TREND);
  const [ttmData, setTtmData] = useState(null);
  const [ttmLoading, setTtmLoading] = useState(false);
  // V25.16 — when the trailing-twelve-month pull fails, the T12 has no data
  // and the `t12_period_matches_engagement` tie correctly refuses the
  // package. Reported on Steele Martin Jones, where the only visible symptom
  // was an opaque failing tie. The reason is held here and surfaced.
  const [ttmError, setTtmError] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showMappingHistory, setShowMappingHistory] = useState(false);
  const [showEngagementSettings, setShowEngagementSettings] = useState(false);
  const [newClient, setNewClient] = useState({ company: '', name: '', email: '', entity_type: 'smllc', industry: '', client_type: 'single', member_client_ids: [] });
  // V20 — Consolidated group envelope (members + accounts/mapping per member).
  // Populated when the selected client has client_type === 'consolidated_group'.
  // Ad-hoc consolidations populate the same state from the preview endpoint.
  const [consolidatedEnvelope, setConsolidatedEnvelope] = useState(null);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  // Ad-hoc consolidation picker
  const [showAdhocConsolidation, setShowAdhocConsolidation] = useState(false);
  const [adhocMemberIds, setAdhocMemberIds] = useState([]);
  // V20.1 — Dashboard date range. Defaults to prior_month (current behavior:
  // backend default). When staff picks Last Quarter / Last Year / Custom,
  // we refetch /qbo/accounts with explicit period_start/period_end and
  // hot-swap accounts + qboMeta so every downstream report reflects the
  // chosen window.
  const [dashboardPeriod, setDashboardPeriod] = useState({ id: 'prior_month', start: null, end: null });
  const [dashboardPeriodLoading, setDashboardPeriodLoading] = useState(false);
  const [dashboardPeriodError, setDashboardPeriodError] = useState(null);

  // V25.23 — the period selector fires on every keystroke of its two
  // <input type="date"> fields, so typing "2026" walked the year through
  // 0002, 0020, 0202 and each half-typed window was FETCHED. QuickBooks
  // answers a nonsense window with HTTP 200 and an empty report, and
  // whichever response landed last won — MEET Ministry ended up holding a
  // package stamped 0002-07-31. Worse, clicking "Custom" starts with both
  // dates empty, and a fetch with no dates is resolved by the backend to the
  // PRIOR MONTH: that is how a January-to-July selection printed an August
  // package.
  //
  // So: refuse a window that is not a plausible date range, wait for the
  // typing to stop, and discard any response that is no longer the selection.
  const periodReqRef = useRef(0);
  const periodDebounceRef = useRef(null);

  const loadDashboardPeriod = async (next, token) => {
    const isCurrent = () => token === periodReqRef.current;
    try {
      const res = await qboAPI.getAccounts(selectedClient.id, next.end || null, next.start || null);
      if (!isCurrent()) return;               // a newer selection is in flight
      setAccounts(res.data.accounts || []);
      setQboMeta({
        reconciliation:   res.data.reconciliation,
        completeness:     res.data.completeness,
        period_start:     res.data.period_start,
        period_end:       res.data.period_end,
        period_start_eve: res.data.period_start_eve,
        ni_at_period_start: res.data.ni_at_period_start,
        cash_at_period_start: res.data.cash_at_period_start,
        period_diagnostics:   res.data.period_diagnostics,
        // V21 — period-scoped QBO P&L (NI, D&A, interest, tax) for the
        // new single-path cash-flow builder. Set by the backend cutover.
        pl_period:         res.data.pl_period,
        pl_month:          res.data.pl_month,
        pl_month_prior:    res.data.pl_month_prior,
        fetch_reports:        res.data.fetch_reports,
      });
      // Refetch TTM with the new period_end so the T12 narrative
      // (and its `t12_ends_on_engagement_period_end` validation tie)
      // align with the chosen window.
      try {
        const ttmRes = await qboAPI.getTTM(selectedClient.id, res.data.period_end);
        if (!isCurrent()) return;
        setTtmData(ttmRes.data);
        setTtmError(null);
      } catch (ttmErr) {
        if (!isCurrent()) return;
        setTtmData(null);
        setTtmError({
          status: ttmErr?.response?.status || null,
          detail: ttmErr?.response?.data?.detail || ttmErr?.message || 'Request failed',
          at: new Date().toISOString(),
        });
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Dashboard period refetch failed:', err);
      setDashboardPeriodError(err?.response?.data?.detail || err.message || 'Failed to load period');
    } finally {
      if (isCurrent()) setDashboardPeriodLoading(false);
    }
  };

  const handleDashboardPeriodChange = (next) => {
    setDashboardPeriod(next);
    setDashboardPeriodError(null);
    if (periodDebounceRef.current) clearTimeout(periodDebounceRef.current);
    if (!selectedClient?.id || selectedClient.client_type === 'consolidated_group') return;
    // Invalidate anything already in flight: its response is now stale.
    const token = ++periodReqRef.current;
    if (!isUsableDashboardPeriod(next)) {
      // A half-typed range is not an error — say nothing and wait.
      setDashboardPeriodLoading(false);
      return;
    }
    setDashboardPeriodLoading(true);
    periodDebounceRef.current = setTimeout(() => {
      loadDashboardPeriod(next, token);
    }, 450);
  };

  // V19.10.6 — Last-sync timestamp displayed in the dashboard top bar.
  // Updated whenever loadData() refreshes the clients list, AND when
  // QBO accounts are re-fetched. Rendered in America/Chicago (Central
  // Time) per user request.
  const [lastSyncAt, setLastSyncAt] = useState(() => new Date());

  useEffect(() => {
    loadData();
  }, []);

  // Persist selected client and view to localStorage
  useEffect(() => {
    if (selectedClient?.id) {
      localStorage.setItem('smj_selected_client', selectedClient.id);
    }
  }, [selectedClient]);

  useEffect(() => {
    localStorage.setItem('smj_view', view);
  }, [view]);

  // Whenever the selected client changes, load their saved COA mapping
  useEffect(() => {
    const loadMappingForClient = async () => {
      if (!selectedClient?.id || selectedClient.id === 'demo') return;
      try {
        const res = await mappingsAPI.get(selectedClient.id);
        if (res.data && res.data.mapping && Object.keys(res.data.mapping).length > 0) {
          setMapping(res.data.mapping);
        }
      } catch (err) {
        // No saved mapping yet — keep template default
        console.log('No saved mapping for client (will use template):', err?.response?.status);
      }
    };
    loadMappingForClient();
  }, [selectedClient]);

  // Fetch trailing-12-month real P&L from QBO when the selected client changes
  const loadTTM = React.useCallback(async (periodEnd = null) => {
    if (!selectedClient?.id || selectedClient.id === 'demo'
        || selectedClient.client_type === 'consolidated_group') {
      setTtmData(null);
      setTtmError(null);
      return;
    }
    setTtmLoading(true);
    try {
      const res = await qboAPI.getTTM(selectedClient.id, periodEnd);
      setTtmData(res.data);
      setTtmError(null);
    } catch (err) {
      const status = err?.response?.status || null;
      setTtmData(null);
      setTtmError({
        status,
        detail: err?.response?.data?.detail || err?.message || 'Request failed',
        at: new Date().toISOString(),
      });
    } finally {
      setTtmLoading(false);
    }
  }, [selectedClient]);

  useEffect(() => {
    loadTTM();
  }, [loadTTM]);

  // V20 — Whenever a consolidated_group is selected, fetch the consolidated envelope.
  useEffect(() => {
    const loadConsolidated = async () => {
      if (selectedClient?.client_type !== 'consolidated_group') {
        setConsolidatedEnvelope(null);
        return;
      }
      setConsolidatedLoading(true);
      try {
        const token = localStorage.getItem('smj_token');
        const resp = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/consolidated/${selectedClient.id}/data`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setConsolidatedEnvelope(resp.data);
      } catch (err) {
        console.error('Consolidated fetch failed:', err);
        setConsolidatedEnvelope({ members: [], group: selectedClient, warnings: {} });
      } finally {
        setConsolidatedLoading(false);
      }
    };
    loadConsolidated();
  }, [selectedClient]);

  const loadData = async () => {
    try {
      const [clientsRes, trendRes] = await Promise.all([
        clientsAPI.list(),
        trendAPI.getData('demo'),
      ]);
      setClients(clientsRes.data);
      setLastSyncAt(new Date());  // V19.10.6 — top-bar timestamp

      // Restore previously selected client + view from localStorage
      const savedClientId = localStorage.getItem('smj_selected_client');
      const savedView = localStorage.getItem('smj_view') || 'companies';
      const savedClient = savedClientId ? clientsRes.data.find(c => c.id === savedClientId) : null;

      if (savedClient && savedView === 'dashboard') {
        setSelectedClient(savedClient);
        setView('dashboard');
        // Re-sync QBO accounts + reconciliation metadata so the publish gate
        // has fresh data after a page reload. Without this hydration, the
        // qbo_bs_recon ties would silently disappear from runValidationTies
        // and the gate would be permissive.
        if (savedClient.qbo_connected) {
          try {
            const res = await qboAPI.getAccounts(savedClient.id);
            setAccounts(res.data.accounts);
            setQboMeta({
              reconciliation: res.data.reconciliation,
              completeness:   res.data.completeness,
              period_start:   res.data.period_start,
              period_end:     res.data.period_end,
              period_start_eve: res.data.period_start_eve,
              ni_at_period_start: res.data.ni_at_period_start,
              cash_at_period_start: res.data.cash_at_period_start,
              period_diagnostics:   res.data.period_diagnostics,
              pl_period:            res.data.pl_period,
              pl_month:             res.data.pl_month,
              pl_month_prior:       res.data.pl_month_prior,
              fetch_reports:        res.data.fetch_reports,
            });
            setStep(2);  // jump straight to Report on rehydrate
          } catch (err) {
            console.log('QBO rehydrate on restore failed:', err?.response?.status);
          }
        }
      } else {
        if (savedClient) setSelectedClient(savedClient);
        setView('companies');
      }
      setTrendData(trendRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = selectedClient?.client_type === 'consolidated_group'
    ? [{ id: 2, label: 'Consolidated Report', done: false }]
    : [
        { id: 0, label: '1 · Import', done: step > 0 },
        { id: 1, label: '2 · Map Accounts', done: step > 1 },
        { id: 2, label: '3 · Report', done: false },
      ];

  const handleImportComplete = (importedAccounts, meta = null) => {
    setAccounts(importedAccounts);
    if (meta) setQboMeta(meta);
    setStep(1);
  };

  const handleRemapComplete = (newMapping) => {
    setMapping(newMapping);
    setStep(2);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.bg }}>
        <Loader2 size={32} className="animate-spin" color={COLORS.primary} />
      </div>
    );
  }

  const handleClientChange = (client) => {
    setSelectedClient(client);
    setShowClientDropdown(false);
    setStep(client.client_type === 'consolidated_group' ? 2 : 0);
    setView('dashboard');
  };

  const handleSelectFromLanding = async (client) => {
    setSelectedClient(client);
    setView('dashboard');

    // V20 — Consolidated groups skip steps 0/1 entirely; the ConsolidatedReportLayer
    // fetches the per-member envelope independently.
    if (client.client_type === 'consolidated_group') {
      setAccounts([]);
      setQboMeta(null);
      setStep(2);
      return;
    }

    // If client is QBO-connected, auto-sync accounts before opening the dashboard
    if (client.qbo_connected && client.id) {
      setLoading(true);
      try {
        const res = await qboAPI.getAccounts(client.id);
        setAccounts(res.data.accounts);
        setQboMeta({
          reconciliation: res.data.reconciliation,
          completeness:   res.data.completeness,
          period_start:   res.data.period_start,
          period_end:     res.data.period_end,
          period_start_eve:     res.data.period_start_eve,
          ni_at_period_start:   res.data.ni_at_period_start,
          cash_at_period_start: res.data.cash_at_period_start,
          period_diagnostics:   res.data.period_diagnostics,
          pl_period:            res.data.pl_period,
          pl_month:             res.data.pl_month,
          pl_month_prior:       res.data.pl_month_prior,
          fetch_reports:        res.data.fetch_reports,
        });
        // Skip Import step — go straight to Map
        setStep(1);
      } catch (err) {
        console.error('Auto-sync on client open failed:', err);
        // Fall back to import step where user can manually sync
        setStep(0);
      } finally {
        setLoading(false);
      }
    } else {
      // Reset accounts to empty so canned data doesn't get mixed in
      setAccounts([]);
      setQboMeta(null);
      setStep(0);
    }
  };

  const handleGoToCompanies = () => {
    setView('companies');
  };

  const handleAddClient = async () => {
    if (!newClient.company || !newClient.name) return;
    if (newClient.client_type === 'consolidated_group' && (!newClient.member_client_ids || newClient.member_client_ids.length < 1)) {
      alert('Select at least one member client for the consolidated group.');
      return;
    }
    try {
      const payload = {
        company: newClient.company,
        name: newClient.name,
        contact_email: newClient.email || null,
        entity_type: newClient.entity_type,
        industry: newClient.industry || null,
        client_type: newClient.client_type || 'single',
      };
      if (payload.client_type === 'consolidated_group') {
        payload.member_client_ids = newClient.member_client_ids;
      }
      await clientsAPI.create(payload);
      // Refresh clients list
      const clientsRes = await clientsAPI.list();
      setClients(clientsRes.data);
      // Select the newly created client
      const created = clientsRes.data.find(c => c.company === newClient.company);
      if (created) setSelectedClient(created);
      // Reset form and close modal
      setNewClient({ company: '', name: '', email: '', entity_type: 'smllc', industry: '', client_type: 'single', member_client_ids: [] });
      setShowAddClientModal(false);
      setShowClientDropdown(false);
    } catch (err) {
      console.error('Failed to create client:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert(`Failed to create client: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    }
  };

  return (
    <div data-testid="staff-dashboard" className="staff-shell" style={{ minHeight: '100vh', background: COLORS.bg }}>
      {/* V24 — Mobile responsiveness lives in /app/frontend/src/styles/mobile.css.
          Class hooks: .staff-shell, .top-bar, .step-nav, .page-padding,
          .report-header, .report-tabs, .kpi-grid, .kpi-auto-grid,
          .statement-card, .statement-meta-row, .statement-table-wrap,
          .bs-grid, .period-picker, .ttm-container, .dash-grid-3,
          .dash-grid-4, .loan-form-grid, .fa-form-grid, .modal-card. */}
      {/* Top Bar */}
      <div className="top-bar" style={{ background: COLORS.dark, borderBottom: `3px solid ${COLORS.primary}`, padding: '0 40px', display: 'flex', alignItems: 'center', gap: 24, height: 80 }}>
        <img 
          src={SMJ_LOGO_URL} 
          alt="Steele Martin Jones" 
          style={{ height: 80, width: 'auto' }}
        />
        <div className="separator" style={{ width: 1, height: 40, background: COLORS.subtle }} />
        
        {/* Client Selector */}
        <div className="client-selector-wrapper" style={{ position: 'relative' }}>
          <button
            data-testid="client-selector"
            onClick={() => setShowClientDropdown(!showClientDropdown)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 14px',
              borderRadius: 8,
              border: `1px solid ${COLORS.subtle}`,
              background: 'rgba(255,255,255,0.05)',
              color: '#fff',
              cursor: 'pointer',
              minWidth: 200,
            }}
          >
            <Building2 size={16} color={COLORS.primary} />
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{selectedClient?.company || 'Select Client'}</div>
              <div style={{ fontSize: 10, color: COLORS.muted }}>{selectedClient?.name}</div>
            </div>
            <ChevronDown size={16} color={COLORS.muted} />
          </button>
          
          {showClientDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: COLORS.card,
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              minWidth: 250,
              zIndex: 100,
              overflow: 'hidden',
            }}>
              {clients.map(c => (
                <button
                  key={c.id}
                  onClick={() => handleClientChange(c)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    border: 'none',
                    background: selectedClient?.id === c.id ? COLORS.primaryLight : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <Building2 size={16} color={selectedClient?.id === c.id ? COLORS.primary : COLORS.muted} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark }}>{c.company}</div>
                    <div style={{ fontSize: 11, color: COLORS.muted }}>{c.name} · {c.entity_type?.toUpperCase() || 'LLC'}</div>
                  </div>
                  {selectedClient?.id === c.id && <Check size={14} color={COLORS.primary} style={{ marginLeft: 'auto' }} />}
                </button>
              ))}
              <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: 8 }}>
                <button
                  data-testid="add-client-btn"
                  onClick={() => {
                    setShowAddClientModal(true);
                    setShowClientDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: 6,
                    border: `1px dashed ${COLORS.border}`,
                    background: 'transparent',
                    color: COLORS.primary,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <Plus size={14} /> Add New Client
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="right-tools" style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="last-sync-pill" style={{
            padding: '4px 10px',
            borderRadius: 6,
            background: COLORS.subtle,
            color: COLORS.muted,
            fontSize: 11,
            fontWeight: 500,
          }}>
            Last sync: {formatCentralTime(lastSyncAt)}
          </span>
          <span className="user-name" style={{ color: '#fff', fontSize: 13 }}>{user?.name}</span>
          <a
            data-testid="contact-support-link"
            href="mailto:firm@steelemartin.com?subject=SMJ%20Financial%20Portal%20Support"
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${COLORS.subtle}`,
              background: 'transparent',
              color: COLORS.muted,
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              textDecoration: 'none',
            }}
          >
            <LifeBuoy size={14} /> Contact Support
          </a>
          {user?.role === 'admin' && (
            <button
              data-testid="recon-page-link"
              onClick={() => navigate('/recon')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: `1px solid ${COLORS.subtle}`,
                background: 'transparent',
                color: COLORS.muted,
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <AlertTriangle size={14} /> Recon
            </button>
          )}
          {user?.role === 'admin' && (
            <button
              data-testid="team-page-link"
              onClick={() => navigate('/team')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: `1px solid ${COLORS.subtle}`,
                background: 'transparent',
                color: COLORS.muted,
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Users size={14} /> Team
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: `1px solid ${COLORS.subtle}`,
              background: 'transparent',
              color: COLORS.muted,
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      {/* Step Nav (only when in dashboard view) */}
      {view === 'dashboard' && (
        <div className="step-nav" style={{ background: COLORS.card, borderBottom: `1px solid ${COLORS.border}`, padding: '0 40px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            data-testid="back-to-companies"
            onClick={handleGoToCompanies}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              background: '#fff',
              color: COLORS.muted,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            ← All Companies
          </button>
          <div className="step-tabs" style={{ display: 'flex' }}>
            {tabs.map(t => (
              <button
                key={t.id}
                data-testid={`step-tab-${t.id}`}
                onClick={() => setStep(t.id)}
                style={{
                  padding: '16px 28px',
                  background: 'none',
                  border: 'none',
                  borderBottom: step === t.id ? `3px solid ${COLORS.primary}` : '3px solid transparent',
                  fontSize: 13,
                  fontWeight: 600,
                  color: step === t.id ? COLORS.primary : t.done ? COLORS.success : COLORS.muted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {t.done && <Check size={14} color={COLORS.success} />}
                {t.label}
              </button>
            ))}
          </div>
          {selectedClient && (
            <div className="step-actions" style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', marginRight: 16 }}>
              <button
                data-testid="open-engagement-settings-btn"
                onClick={() => setShowEngagementSettings(true)}
                title="Configure engagement type, framework, and disclosures for this client"
                style={{
                  padding: '8px 14px',
                  background: 'none', border: `1px solid ${COLORS.border}`,
                  color: COLORS.muted, borderRadius: 4, fontSize: 12,
                  fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Settings size={14} /> Engagement Settings
              </button>
              <button
                data-testid="open-mapping-history-btn"
                onClick={() => setShowMappingHistory(true)}
                title="View prior mapping versions for this client"
                style={{
                  padding: '8px 14px',
                  background: 'none', border: `1px solid ${COLORS.border}`,
                  color: COLORS.muted, borderRadius: 4, fontSize: 12,
                  fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <History size={14} /> Mapping History
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div style={{ padding: view === 'companies' ? 0 : '40px 40px 80px' }}>
        {view === 'companies' && (
          <CompaniesLandingPage
            clients={clients}
            onSelectClient={handleSelectFromLanding}
            onAddClient={() => setShowAddClientModal(true)}
            onRefreshClients={async () => {
              try {
                const clientsRes = await clientsAPI.list();
                setClients(clientsRes.data);
              } catch (err) {
                console.error('Refresh clients failed:', err);
              }
            }}
          />
        )}
        {view === 'dashboard' && step === 0 && <ImportLayer onComplete={handleImportComplete} clientId={selectedClient?.id || 'demo'} />}
        {view === 'dashboard' && step === 1 && (
          <FathomMapLayer
            onComplete={handleRemapComplete}
            accounts={accounts}
            clientId={selectedClient?.id || 'demo'}
          />
        )}
        {view === 'dashboard' && step === 2 && (
          selectedClient?.client_type === 'consolidated_group'
            ? (consolidatedLoading
                ? <div style={{ padding: 40, textAlign: 'center', color: COLORS.muted }}>
                    <Loader2 size={28} className="animate-spin" /> Loading consolidated data…
                  </div>
                : <ConsolidatedReportLayer
                    envelope={consolidatedEnvelope}
                    group={selectedClient}
                    client={selectedClient}
                  />)
            : <ReportLayer accounts={accounts} mapping={mapping} client={selectedClient} trendData={trendData} ttmData={ttmData} ttmLoading={ttmLoading} ttmError={ttmError} onRetryTtm={() => loadTTM(qboMeta?.period_end || null)} qboMeta={qboMeta} dashboardPeriod={dashboardPeriod} onPeriodChange={handleDashboardPeriodChange} periodLoading={dashboardPeriodLoading} periodError={dashboardPeriodError} />
        )}
      </div>

      {/* Add Client Modal */}
      {showMappingHistory && selectedClient && (
        <MappingHistoryModal
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          onClose={() => setShowMappingHistory(false)}
          onReverted={() => { window.location.reload(); }}
        />
      )}

      {showEngagementSettings && selectedClient && (
        <EngagementSettingsModal
          client={selectedClient}
          totalAssets={(() => {
            try {
              return (
                sectionTotal(accounts, mapping, 'cash') +
                sectionTotal(accounts, mapping, 'assets_current') +
                sectionTotal(accounts, mapping, 'assets_fixed') +
                sectionTotal(accounts, mapping, 'assets_other')
              );
            } catch (e) { return 0; }
          })()}
          onClose={() => setShowEngagementSettings(false)}
          onSaved={(updatedClient) => {
            // Update the local selectedClient + clients[] so downstream
            // renders (PrintModal, KPI strip, etc.) immediately reflect
            // the new framework / engagement_type without a full reload.
            setSelectedClient(updatedClient);
            setClients(clients.map(c => c.id === updatedClient.id ? updatedClient : c));
          }}
        />
      )}

      {showAddClientModal && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => e.target === e.currentTarget && setShowAddClientModal(false)}
        >
          <div className="modal-card" style={{
            background: '#fff',
            borderRadius: 12,
            width: '100%',
            maxWidth: 480,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: COLORS.dark, margin: 0 }}>Add New Client</h3>
              <button onClick={() => setShowAddClientModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.muted }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {/* V20 — Client type selector. Consolidated groups skip QBO setup. */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>Client Type *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { id: 'single', label: 'Single Entity', desc: 'One QBO file' },
                    { id: 'consolidated_group', label: 'Consolidated Group', desc: 'Roll-up of 2+ members' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      data-testid={`new-client-type-${opt.id}`}
                      onClick={() => setNewClient({ ...newClient, client_type: opt.id })}
                      style={{
                        padding: '10px 12px', borderRadius: 8, textAlign: 'left',
                        border: `2px solid ${newClient.client_type === opt.id ? COLORS.primary : COLORS.border}`,
                        background: newClient.client_type === opt.id ? '#E0F7FA' : '#fff',
                        color: newClient.client_type === opt.id ? COLORS.primary : COLORS.dark,
                        fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontSize: 13 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, fontWeight: 400, color: COLORS.muted, marginTop: 2 }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>{newClient.client_type === 'consolidated_group' ? 'Group Name *' : 'Company Name *'}</label>
                <input
                  type="text"
                  value={newClient.company}
                  onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                  placeholder="Enter company name"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>Contact Name *</label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Enter contact name"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>Email</label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="Enter email address"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14 }}
                />
              </div>
              {newClient.client_type !== 'consolidated_group' && (
                <>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>Entity Type *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {[
                    { id: 'sole_prop', label: 'Sole Prop' },
                    { id: 'smllc', label: 'SMLLC' },
                    { id: 'multi_llc', label: 'Multi-Member LLC' },
                    { id: 's_corp', label: 'S-Corp' },
                    { id: 'c_corp', label: 'C-Corp' },
                    { id: 'nonprofit', label: 'Not-for-Profit' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      data-testid={`new-client-entity-${opt.id}`}
                      onClick={() => setNewClient({ ...newClient, entity_type: opt.id })}
                      style={{
                        padding: '10px 8px',
                        borderRadius: 8,
                        border: `2px solid ${newClient.entity_type === opt.id ? COLORS.primary : COLORS.border}`,
                        background: newClient.entity_type === opt.id ? '#E0F7FA' : '#fff',
                        color: newClient.entity_type === opt.id ? COLORS.primary : COLORS.dark,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>Industry *</label>
                <select
                  value={newClient.industry || ''}
                  onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, background: '#fff' }}
                >
                  <option value="">Select Industry...</option>
                  <option value="agriculture">Agriculture & Farming</option>
                  <option value="construction">Construction</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="professional">Professional Services</option>
                  <option value="real_estate">Real Estate</option>
                  <option value="retail">Retail</option>
                  <option value="restaurant">Restaurant & Food Service</option>
                  <option value="technology">Technology</option>
                  <option value="transportation">Transportation & Logistics</option>
                  <option value="wholesale">Wholesale Distribution</option>
                  <option value="other">Other</option>
                </select>
              </div>
                </>
              )}
              {newClient.client_type === 'consolidated_group' && (
                <div style={{ marginBottom: 20 }} data-testid="consolidated-member-picker">
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>Member Clients *</label>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 8 }}>
                    Select 2+ existing clients to roll up. Nested groups are not allowed.
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 6 }}>
                    {clients
                      .filter(c => c.client_type !== 'consolidated_group')
                      .map(c => {
                        const isChecked = newClient.member_client_ids.includes(c.id);
                        return (
                          <label key={c.id} data-testid={`member-option-${c.id}`}
                                 style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                                          cursor: 'pointer', borderRadius: 4,
                                          background: isChecked ? COLORS.primaryLight : 'transparent' }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                const set = new Set(newClient.member_client_ids);
                                if (set.has(c.id)) set.delete(c.id); else set.add(c.id);
                                setNewClient({ ...newClient, member_client_ids: Array.from(set) });
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, color: COLORS.dark, fontWeight: 500 }}>{c.company}</div>
                              <div style={{ fontSize: 11, color: COLORS.muted }}>{c.name} · {c.qbo_connected ? 'QBO Connected' : 'No QBO'}</div>
                            </div>
                          </label>
                        );
                      })}
                    {clients.filter(c => c.client_type !== 'consolidated_group').length === 0 && (
                      <div style={{ color: COLORS.muted, padding: 16, fontSize: 12, textAlign: 'center' }}>
                        No member-eligible clients available. Create single clients first.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 12, flexShrink: 0 }}>
              <button
                onClick={() => setShowAddClientModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 8,
                  border: `2px solid ${COLORS.border}`,
                  background: '#fff',
                  color: COLORS.dark,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddClient}
                disabled={!newClient.company || !newClient.name || (newClient.client_type === 'consolidated_group' && newClient.member_client_ids.length < 1)}
                data-testid="create-client-btn"
                style={{
                  flex: 2,
                  padding: '12px',
                  borderRadius: 8,
                  border: 'none',
                  background: (!newClient.company || !newClient.name || (newClient.client_type === 'consolidated_group' && newClient.member_client_ids.length < 1)) ? COLORS.muted : COLORS.primary,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: (!newClient.company || !newClient.name || (newClient.client_type === 'consolidated_group' && newClient.member_client_ids.length < 1)) ? 'not-allowed' : 'pointer',
                }}
              >
                Create Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
